import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { GET, POST, PUT, DELETE, loading, alert, format_date, Select, saveAs, DateInput } from "../components/CustomComponent";
import html2canvas from "html2canvas";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import ThaiBahtText from "thai-baht-text";
import JSZip from "jszip";
import companyLogo from "../assets/images/logo512.png";

// pdfmake 0.3.x export vfs_fonts เป็น vfs object ตรง ๆ
pdfMake.vfs = pdfFonts;

const API = "v1/admin/company-documents";

// ประเภทเอกสาร (เมนูเดียว เลือกประเภทได้) เรียงตามลำดับงานจริง: เสนอราคา -> วางบิล -> ใบกำกับภาษี -> ใบเสร็จ -> ใบสำคัญรับ
const DOC_TYPES = [
  { value: "quotation", th: "ใบเสนอราคา", en: "QUOTATION", icon: "fa-solid fa-file-lines", color: "#7b3fc0" },
  { value: "billing_note", th: "ใบวางบิล", en: "INVOICE", icon: "fa-solid fa-file-invoice-dollar", color: "#0f6bdc" },
  { value: "tax_invoice", th: "ใบกำกับภาษี", en: "TAX INVOICE", icon: "fa-solid fa-file-invoice", color: "#4f46e5" },
  { value: "receipt", th: "ใบเสร็จรับเงิน", en: "RECEIPT", icon: "fa-solid fa-receipt", color: "#10b981" },
  { value: "receipt_voucher", th: "ใบสำคัญรับ", en: "RECEIPT VOUCHER", icon: "fa-solid fa-hand-holding-dollar", color: "#0ea5e9" },
  { value: "purchase_order", th: "ใบสั่งซื้อ", en: "PURCHASE ORDER", icon: "fa-solid fa-cart-shopping", color: "#f59e0b" },
  { value: "payment_voucher", th: "ใบสำคัญจ่าย", en: "PAYMENT VOUCHER", icon: "fa-solid fa-money-check-dollar", color: "#dc2626" },
];
// กลุ่มในแถบเลือกประเภทของหน้ารายการ: แยกเอกสารฝั่งขาย/รับเงิน กับฝั่งซื้อ/จ่ายเงิน ให้หาเจอเร็วเมื่อประเภทเยอะขึ้น
const DOC_GROUPS = [
  { key: "income", label: "ฝั่งขาย / รับเงิน", types: ["quotation", "billing_note", "tax_invoice", "receipt", "receipt_voucher"] },
  { key: "expense", label: "ฝั่งซื้อ / จ่ายเงิน", types: ["purchase_order", "payment_voucher"] },
];
const docTypeInfo = (v) => DOC_TYPES.find((d) => d.value === v) || DOC_TYPES.find((d) => d.value === "billing_note");
// ใบสำคัญรับ/จ่าย ใช้โครงสร้างเอกสารและรายการต่างจากเอกสารอื่น (ไม่มี VAT, รายการอ้างอิงเอกสาร ไม่ใช่ qty/unit_price)
const isVoucher = (docType) => docType === "payment_voucher" || docType === "receipt_voucher";
// ใบสั่งซื้อ: บริษัทเป็นฝ่ายสั่งซื้อ อีกฝ่ายเป็นผู้ขาย (ใช้ช่อง customer_* เก็บข้อมูลผู้ขาย) และไม่มีช่องทางรับชำระเงินของบริษัท
const isPurchaseOrder = (docType) => docType === "purchase_order";
// ใบกำกับภาษี: ต้องมี VAT เสมอ และตามปกติไม่แสดงหัก ณ ที่จ่าย (ผู้จ่ายเป็นฝ่ายหักตอนชำระ) จึงปิด WHT เป็นค่าเริ่มต้น แต่ยังเปิดเองได้
const isTaxInvoice = (docType) => docType === "tax_invoice";
// ใบเสร็จ/ใบกำกับภาษี ออกต่อจากใบวางบิลได้ จึงมีช่องดึงข้อมูลลูกค้า/รายการจากใบวางบิลมาเติม
const canPullFromBilling = (docType) => docType === "receipt" || isTaxInvoice(docType);
// หัวคอลัมน์คู่สัญญาในตารางรายการ เปลี่ยนตามประเภท (ลูกค้า / ผู้ขาย / รับเงินจาก / จ่ายให้แก่)
const listPartyHeader = (docType) => (isVoucher(docType) ? voucherPartyLabel(docType) : partyLabels(docType).party);
const partyLabels = (docType) =>
  isPurchaseOrder(docType)
    ? { issuer: "ผู้สั่งซื้อ", party: "ผู้ขาย/ผู้จำหน่าย", short: "ผู้ขาย", pick: "-- เลือกผู้ขายที่บันทึกไว้ (ไม่บังคับ) --" }
    : { issuer: "ผู้ออกบิล", party: "ลูกค้า/ผู้รับบริการ", short: "ลูกค้า", pick: "-- เลือกลูกค้าที่บันทึกไว้ (ไม่บังคับ) --" };

// สไตล์แท็บ/การ์ดสรุป ใช้ธีมหลักของเว็บ (gradient น้ำเงิน-ฟ้า เดียวกับ .btn-primary ใน style.css)
const BRAND_COLOR = "#0c76e5";
// การ์ดสรุปชุดเดียวกับหน้าธุรกรรมการเงิน / เงินเดือน
const SUMMARY_CARD_STYLE = {
  background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)",
};
// ไอคอนใช้สีประจำประเภทเอกสาร ชุดเดียวกับ badge ประเภทในตาราง (พื้นจางด้วย alpha 1f)
const summaryIconStyle = (color = BRAND_COLOR) => ({
  width: 44,
  height: 44,
  borderRadius: 12,
  background: `${color}1f`,
  color,
  fontSize: 18,
});
// แท็บที่ไม่ได้เลือก: พื้นขาวขอบฟ้าอ่อน (radius/padding มาจาก class .btn ของธีม ไม่กำหนดทับ)
const TAB_IDLE_STYLE = {
  background: "#fff",
  color: "#22384a",
  border: "1px solid rgba(18, 125, 255, 0.12)",
  boxShadow: "0 4px 12px rgba(12, 118, 229, 0.04)",
};
const TAB_BADGE = {
  active: { background: "rgba(255,255,255,0.25)", color: "#fff" },
  idle: { background: "rgba(12, 117, 230, 0.10)", color: "#0c75e6" },
};
// กล่องขาวขอบฟ้าอ่อน ใช้กับแถบประเภท / filter / ตาราง ให้เป็นชุดเดียวกัน
const PANEL_STYLE = { border: "1px solid rgba(18,125,255,0.10)", boxShadow: "0 4px 12px rgba(12,118,229,0.04)" };
// รายการประเภทเอกสารในแถบด้านซ้าย (ยังไม่ได้เลือก): โปร่ง ไม่มีเงา ให้ดูเป็นเมนูรายการ ไม่ใช่ปุ่มลอยหลายอัน
const NAV_IDLE_STYLE = { background: "transparent", color: "#22384a", border: "1px solid transparent", boxShadow: "none" };

const todayIso = () => new Date().toISOString().slice(0, 10);
// yyyy-mm-dd ตามเวลาเครื่อง (ไม่ใช้ toISOString เพราะจะเพี้ยนไป 1 วันเมื่อ timezone เป็น +07)
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// ค่าเริ่มต้นของ filter = ช่วงวันที่ของเดือนปัจจุบัน (วันที่ 1 ถึงวันสุดท้ายของเดือน)
const currentMonthRange = () => {
  const now = new Date();
  return {
    date_from: isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)),
    date_to: isoLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};
const defaultFilters = () => ({ searchText: "", ...currentMonthRange() });
// ช่วงวันที่สำเร็จรูป ("ทั้งหมด" = ไม่จำกัดวันที่ ใช้ตอนอยากค้นข้ามเดือน)
const QUICK_RANGES = [
  { key: "this_month", label: "เดือนนี้", range: () => currentMonthRange() },
  {
    key: "last_month",
    label: "เดือนที่แล้ว",
    range: () => {
      const now = new Date();
      return {
        date_from: isoLocal(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        date_to: isoLocal(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    },
  },
  {
    key: "this_year",
    label: "ปีนี้",
    range: () => {
      const y = new Date().getFullYear();
      return { date_from: `${y}-01-01`, date_to: `${y}-12-31` };
    },
  },
  { key: "all", label: "ทั้งหมด", range: () => ({ date_from: "", date_to: "" }) },
];
// ค้นจาก เลขที่ / ชื่อลูกค้า / เลขภาษี / วัตถุประสงค์ / หมายเหตุ
const matchSearch = (d, q) =>
  !q || [d.doc_no, d.customer_name, d.customer_tax_id, d.purpose, d.note].some((v) => String(v || "").toLowerCase().includes(q));
const money = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// dd/mm/yyyy ค.ศ. เช่น 01/06/2026 (มาตรฐานวันที่ของทั้งระบบ)
const docDate = (iso) => (iso ? format_date(`${iso}T00:00:00`, "dd/mm/yyyy") : "");

// เลขที่เอกสารในแต่ละรายการของใบสำคัญรับ/จ่าย ใช้เลขเดียวกับเลขที่เอกสาร (doc_no) เสมอ ไม่แยกกรอก
const emptyItem = (docType) =>
  isVoucher(docType) ? { doc_date: todayIso(), description: "", amount: 0 } : { name: "", detail: "", qty: 1, unit_price: 0 };
const emptyDoc = (doc_type = "billing_note") => ({
  uid: "",
  doc_type,
  doc_no: "",
  doc_date: todayIso(),
  purpose: "",
  company_name: "",
  company_tax_id: "",
  company_address: "",
  customer_name: "",
  customer_tax_id: "",
  customer_address: "",
  items: [emptyItem(doc_type)],
  vat_enabled: !isVoucher(doc_type),
  vat_percent: 7,
  wht_enabled: !isPurchaseOrder(doc_type) && !isTaxInvoice(doc_type),
  wht_percent: 3,
  wht_amount: 0,
  payment_account_name: "",
  payment_account_no: "",
  payment_bank: "",
  signer_name: "",
  signer_employee_uid: "", // พนักงานที่เป็นผู้รับเงิน/ผู้จ่ายเงิน (ใบสำคัญ) ใช้ดึงลายเซ็นมาแสดง
  signer_signature_url: "", // data url ของลายเซ็น ไม่ได้เก็บใน DB โหลดใหม่ทุกครั้งที่แสดงเอกสาร
  note: "",
});

// แปลงเอกสารจาก API -> state ฟอร์ม
const mapApiDoc = (d) => ({
  uid: d.uid,
  doc_type: d.doc_type,
  doc_no: d.doc_no || "",
  doc_date: d.doc_date || todayIso(),
  purpose: d.purpose || "",
  company_name: d.company_name || "",
  company_tax_id: d.company_tax_id || "",
  company_address: d.company_address || "",
  customer_name: d.customer_name || "",
  customer_tax_id: d.customer_tax_id || "",
  customer_address: d.customer_address || "",
  items: (d.items && d.items.length ? d.items : [emptyItem(d.doc_type)]).map((it) =>
    isVoucher(d.doc_type)
      ? { doc_date: it.doc_date || "", description: it.description || "", amount: Number(it.amount) || 0 }
      : { name: it.name || "", detail: it.detail || "", qty: Number(it.qty) || 0, unit_price: Number(it.unit_price) || 0 }
  ),
  vat_enabled: d.vat_enabled !== false,
  vat_percent: Number(d.vat_percent) || 0,
  wht_enabled: d.wht_enabled !== false,
  wht_percent: Number(d.wht_percent) || 0,
  wht_amount: Number(d.wht_amount) || 0,
  payment_account_name: d.payment_account_name || "",
  payment_account_no: d.payment_account_no || "",
  payment_bank: d.payment_bank || "",
  signer_name: d.signer_name || "",
  signer_employee_uid: d.signer_employee_uid || "",
  signer_signature_url: "",
  note: d.note || "",
});

// ปัดเป็นสตางค์ (toFixed ก่อนปัด กัน 8478.505 * 100 กลายเป็น 847850.4999...)
const roundSatang = (n) => Math.round(Number(((Number(n) || 0) * 100).toFixed(6))) / 100;

const computeTotals = (doc) => {
  const items = doc.items || [];
  const voucher = isVoucher(doc.doc_type);
  const subtotal = roundSatang(voucher
    ? items.reduce((s, it) => s + (Number(it.amount) || 0), 0)
    : items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0));
  let vat_amount = !voucher && doc.vat_enabled ? roundSatang((subtotal * (Number(doc.vat_percent) || 0)) / 100) : 0;
  // ราคาต่อหน่วยที่ถอดมาจากยอดรวม VAT (เช่น 129,600 / 1.07 = 121,121.50) ทำให้ยอดรวมเพี้ยน .01/.99 → ขยับ VAT 1 สตางค์ให้ยอดรวมเป็นบาทเต็ม
  if (vat_amount) {
    const gross = roundSatang(subtotal + vat_amount);
    const drift = roundSatang(gross - Math.round(gross));
    if (drift !== 0 && Math.abs(drift) <= 0.01) vat_amount = roundSatang(vat_amount - drift);
  }
  const wht_amount = roundSatang(voucher ? Number(doc.wht_amount) || 0 : doc.wht_enabled ? (subtotal * (Number(doc.wht_percent) || 0)) / 100 : 0);
  const grand_total = roundSatang(subtotal + vat_amount - wht_amount);
  return { subtotal, vat_amount, wht_amount, grand_total, amount_text: ThaiBahtText(grand_total) };
};

// รวมที่อยู่จากข้อมูลองค์กร
const composeAddress = (c) =>
  [c.address, c.sub_district && `ตำบล${c.sub_district}`, c.district && `อำเภอ${c.district}`, c.province && `จังหวัด${c.province}`, c.zip_code]
    .filter(Boolean)
    .join(" ");

// ป้ายกำกับผู้รับ/ผู้จ่าย และผู้ลงนามท้ายเอกสาร แยกตามประเภทใบสำคัญ
const voucherPartyLabel = (docType) => (docType === "receipt_voucher" ? "รับเงินจาก" : "จ่ายให้แก่");
const voucherSigners = (docType) => [
  { label: "ผู้จัดทำ" },
  { label: "ผู้ตรวจสอบ" },
  { label: "ผู้อนุมัติ" },
  // payee = ช่องที่เลือกพนักงานเพื่อดึงลายเซ็นมาแสดงได้
  { label: docType === "receipt_voucher" ? "ผู้จ่ายเงิน" : "ผู้รับเงิน", payee: true },
];

// ---- ขนาดหน้ากระดาษ A4 ที่ 96dpi (794 x 1123 px) ใช้ทั้ง preview และ PDF ----
// เส้นตารางรายการในเอกสาร (เข้มพอให้เห็นชัดเมื่อพิมพ์/แปลงเป็น PDF)
const GRID_LINE = "1px solid #6b7280";
// โลโก้บริษัทที่หัวเอกสาร: สูง 64px (~17mm บน A4) ไม่เกินความสูงบล็อกชื่อเอกสาร กว้างไม่เกิน 150px กันโลโก้แนวนอนกินพื้นที่
const LOGO_STYLE = { height: 64, maxWidth: 150, objectFit: "contain", flexShrink: 0 };
const A4_W = 794;
const A4_H = 1123;
const PAGE_PAD = 48;
const PAGE_CONTENT_H = A4_H - PAGE_PAD * 2; // พื้นที่เนื้อหาต่อหน้า
const ITEMS_GAP = 16; // margin ด้านบนของตารางรายการ
const FIT_BUFFER = 6; // กันคลาดเคลื่อนจากการปัดเศษ/ฟอนต์

// แบ่งรายการลงหน้า A4 ตามความสูงจริงที่วัดได้ (หน้าแรกมีหัวเอกสารเต็ม หน้าถัดไปใช้หัวแบบย่อ)
function buildPages({ headerH, contHeaderH, theadH, footerH, rowH }) {
  const n = rowH.length;
  const pages = [];
  let i = 0;
  let first = true;
  for (;;) {
    const avail = PAGE_CONTENT_H - (first ? headerH : contHeaderH) - (n ? ITEMS_GAP + theadH : 0) - FIT_BUFFER;
    const rows = [];
    let used = 0;
    while (i < n) {
      if (used + rowH[i] > avail && rows.length) break;
      rows.push(i);
      used += rowH[i];
      i += 1;
      if (used > avail) break; // แถวเดียวสูงเกินหน้า ปล่อยให้ล้นแทนวนไม่จบ
    }
    const done = i >= n;
    const footerFits = done && used + footerH <= avail;
    pages.push({ first, rows, footer: footerFits });
    if (done) {
      if (!footerFits) pages.push({ first: false, rows: [], footer: true });
      break;
    }
    first = false;
  }
  return pages;
}

// วัดความสูงของแต่ละส่วน แล้วคืน pages ที่แบ่งแล้ว (null = ยังวัดไม่เสร็จ)
function usePaginatedDoc(parts, deps) {
  const measureRef = useRef(null);
  const [pages, setPages] = useState(null);
  useLayoutEffect(() => {
    const root = measureRef.current;
    if (!root) return;
    const heightOf = (sel) => {
      const el = root.querySelector(sel);
      return el ? el.getBoundingClientRect().height : 0;
    };
    setPages(
      buildPages({
        headerH: heightOf('[data-m="header"]'),
        contHeaderH: heightOf('[data-m="cont-header"]'),
        theadH: heightOf('[data-m="thead"]'),
        footerH: heightOf('[data-m="footer"]'),
        rowH: Array.from(root.querySelectorAll('[data-m="row"]')).map((el) => el.getBoundingClientRect().height),
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { measureRef, pages };
}

// กรอบกระดาษ A4 หนึ่งหน้า (ความสูงคงที่ ไม่ยืดตามเนื้อหา)
function A4Page({ children, fontSize, lineHeight, index, total, shadow }) {
  return (
    <div
      data-a4page=""
      style={{ width: A4_W, height: A4_H, background: "#fff", color: "#111", padding: PAGE_PAD, fontSize, lineHeight, boxSizing: "border-box", position: "relative", overflow: "hidden", ...(shadow ? { boxShadow: "0 8px 28px rgba(15,23,42,0.18)" } : {}) }}
    >
      {children}
      {total > 1 && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 18, textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
          หน้า {index + 1} / {total}
        </div>
      )}
    </div>
  );
}

// หัวเอกสารแบบย่อสำหรับหน้าที่ 2 เป็นต้นไป
function ContinuedHeader({ doc }) {
  const info = docTypeInfo(doc.doc_type);
  return (
    <div style={{ display: "flow-root" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", borderBottom: "1px solid #cbd5e1", paddingBottom: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>
          {info.th} <span style={{ fontSize: 14, fontWeight: 400, color: "#6b7280" }}>(ต่อ)</span>
        </div>
        <div style={{ fontSize: 14, color: "#374151" }}>
          เลขที่ {doc.doc_no || "-"} · วันที่ {docDate(doc.doc_date)}
        </div>
      </div>
    </div>
  );
}

// ---- ส่วนประกอบใบสำคัญรับ/จ่าย ----
function voucherParts(doc) {
  const info = docTypeInfo(doc.doc_type);
  const t = computeTotals(doc);
  const header = (
    <div data-m="header" style={{ display: "flow-root" }}>
      <div style={{ textAlign: "center" }}>
        {/* โลโก้บริษัทกึ่งกลาง เหนือชื่อบริษัท (หัวใบสำคัญจัดกึ่งกลางทั้งบล็อก) */}
        <img src={companyLogo} alt="" style={{ ...LOGO_STYLE, height: 56, marginBottom: 6 }} />
        <div style={{ fontSize: 22, fontWeight: 800 }}>{doc.company_name || ""}</div>
        {doc.company_address ? <div style={{ marginTop: 2 }}>{doc.company_address}</div> : null}
        {doc.company_tax_id ? <div style={{ marginTop: 2 }}>เลขประจำตัวผู้เสียภาษี {doc.company_tax_id}</div> : null}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 20, paddingTop: 10, borderTop: "1px solid #6b7280" }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, color: info.color, lineHeight: 1 }}>{info.th}</div>
          <div style={{ fontSize: 15, color: "#6b7280" }}>{info.en}</div>
        </div>
        <table style={{ fontSize: 14 }}>
          <tbody>
            <tr><td style={{ fontWeight: 700, paddingRight: 8 }}>เลขที่</td><td>{doc.doc_no || "-"}</td></tr>
            <tr><td style={{ fontWeight: 700, paddingRight: 8 }}>วันที่</td><td>{docDate(doc.doc_date)}</td></tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <span style={{ fontWeight: 700, flexShrink: 0 }}>{voucherPartyLabel(doc.doc_type)}:</span>
        <span style={{ flex: 1, borderBottom: "1px dotted #94a3b8" }}>{doc.customer_name || ""}</span>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <span style={{ fontWeight: 700, flexShrink: 0 }}>เพื่อชำระ:</span>
        <span style={{ flex: 1, borderBottom: "1px dotted #94a3b8", whiteSpace: "pre-wrap" }}>{doc.purpose || ""}</span>
      </div>
    </div>
  );

  const rows = doc.items.map((it, idx) => (
    <tr key={idx} data-m="row" style={{ verticalAlign: "top" }}>
      <td style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center" }}>{it.doc_date ? docDate(it.doc_date) : ""}</td>
      <td style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center" }}>{doc.doc_no || ""}</td>
      <td style={{ border: GRID_LINE, padding: "8px 10px", whiteSpace: "pre-wrap" }}>{it.description || ""}</td>
      <td style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "right", fontWeight: 600 }}>{it.amount ? money(it.amount) : ""}</td>
    </tr>
  ));

  const table = (rowNodes) => (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: ITEMS_GAP }}>
      <thead data-m="thead">
        <tr style={{ background: "#6b7280", color: "#fff" }}>
          <th style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center", width: 100 }}>วันที่เอกสาร</th>
          <th style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center", width: 120 }}>เลขที่เอกสาร</th>
          <th style={{ border: GRID_LINE, padding: "8px 10px", textAlign: "center" }}>รายการ / Description</th>
          <th style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center", width: 110 }}>จำนวนเงิน</th>
        </tr>
      </thead>
      <tbody>{rowNodes}</tbody>
    </table>
  );

  const footer = (
    <div data-m="footer" style={{ display: "flow-root" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 24, marginTop: 20 }}>
        <div style={{ flex: 1 }}>
          <div><span style={{ fontWeight: 700 }}>หมายเหตุ:&nbsp;&nbsp;</span>{doc.note || ""}</div>
          <div style={{ marginTop: 10 }}><span style={{ fontWeight: 700 }}>จำนวนเงิน:&nbsp;&nbsp;</span><span style={{ fontStyle: "italic" }}>({t.amount_text})</span></div>
        </div>
        <div style={{ width: 280 }}>
          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0", padding: "4px 0" }}>
            <span>จำนวนเงินรวม</span><span>{money(t.subtotal)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0", padding: "4px 0" }}>
            <span>หัก ณ ที่จ่าย</span><span>{t.wht_amount ? money(t.wht_amount) : ""}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontWeight: 700, color: "#dc2626" }}>
            <span>คงเหลือสุทธิ</span><span>{money(t.grand_total)}</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 64, textAlign: "center" }}>
        {voucherSigners(doc.doc_type).map((s, i) => (
          // ช่องผู้รับเงินกว้างกว่าช่องอื่น เพื่อให้ลายเซ็นแสดงใหญ่และอ่านออกชัด
          <div key={i} style={{ width: s.payee ? 210 : 150 }}>
            {/* ช่องผู้รับเงิน/ผู้จ่ายเงิน วางลายเซ็นพนักงานที่เลือกไว้บนเส้น */}
            <div style={{ borderBottom: "1px dotted #94a3b8", height: 72, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
              {s.payee && doc.signer_signature_url ? (
                <img src={doc.signer_signature_url} alt="" style={{ maxHeight: 70, maxWidth: 210, objectFit: "contain" }} />
              ) : null}
            </div>
            <div style={{ marginTop: 6 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return { header, table, rows, footer, fontSize: 14, lineHeight: 1.4 };
}

// ---- ส่วนประกอบเอกสารทั่วไป (ใบวางบิล/ใบเสนอราคา/ใบเสร็จ) ----
function standardParts(doc) {
  const info = docTypeInfo(doc.doc_type);
  const labels = partyLabels(doc.doc_type);
  const t = computeTotals(doc);
  const header = (
    <div data-m="header" style={{ display: "flow-root" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        {/* โลโก้บริษัทมุมซ้ายบน คู่กับชื่อเอกสาร */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src={companyLogo} alt="" style={LOGO_STYLE} />
          <div>
            <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1 }}>{info.th}</div>
            <div style={{ fontSize: 18, color: "#6b7280" }}>{info.en}</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 700 }}>{doc.doc_no || "-"}</div>
          <div style={{ fontWeight: 700 }}>วันที่: {docDate(doc.doc_date)}</div>
        </div>
      </div>

      {/* ผู้ออกบิล / ผู้สั่งซื้อ */}
      <table style={{ width: "100%", marginTop: 20, fontSize: 14 }}>
        <tbody>
          <tr><td style={{ fontWeight: 700, width: 150, verticalAlign: "top" }}>{labels.issuer}:</td><td>{doc.company_name || ""}</td></tr>
          <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>เลขประจำตัวผู้เสียภาษี:</td><td>{doc.company_tax_id || ""}</td></tr>
          <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>ที่อยู่:</td><td>{doc.company_address || ""}</td></tr>
        </tbody>
      </table>
      <hr style={{ border: "none", borderTop: "1px solid #6b7280", margin: "12px 0" }} />
      {/* ลูกค้า/ผู้รับบริการ หรือ ผู้ขาย (ใบสั่งซื้อ) */}
      <table style={{ width: "100%", marginTop: 12, paddingTop: 10, fontSize: 14 }}>
        <tbody>
          <tr><td style={{ fontWeight: 700, width: 150, verticalAlign: "top" }}>{labels.party}:</td><td>{doc.customer_name || ""}</td></tr>
          <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>เลขประจำตัวผู้เสียภาษี:</td><td>{doc.customer_tax_id || ""}</td></tr>
          <tr><td style={{ fontWeight: 700, verticalAlign: "top" }}>ที่อยู่:</td><td>{doc.customer_address || ""}</td></tr>
        </tbody>
      </table>
    </div>
  );

  const rows = doc.items.map((it, idx) => {
    const amount = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
    return (
      <tr key={idx} data-m="row" style={{ verticalAlign: "top" }}>
        <td className="align-top" style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center" }}>{idx + 1}</td>
        <td className="align-top" style={{ border: GRID_LINE, padding: "8px 10px" }}>
          <div style={{ fontWeight: 700, whiteSpace: "pre-wrap" }}>{it.name || ""}</div>
          {it.detail ? <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap", marginTop: 2 }}>{it.detail}</div> : null}
        </td>
        <td className="align-top" style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center" }}>{it.qty ? Number(it.qty).toLocaleString("en-US") : ""}</td>
        <td className="align-top" style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "right" }}>{it.unit_price ? money(it.unit_price) : ""}</td>
        <td className="align-top" style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "right", fontWeight: 600 }}>{amount ? money(amount) : ""}</td>
      </tr>
    );
  });

  const table = (rowNodes) => (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: ITEMS_GAP }}>
      <thead data-m="thead">
        <tr style={{ background: "#6b7280", color: "#fff" }}>
          <th style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center", width: 50 }}>ลำดับ</th>
          <th style={{ border: GRID_LINE, padding: "8px 10px", textAlign: "center" }}>รายการ</th>
          <th style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center", width: 70 }}>จำนวน</th>
          <th style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center", width: 100 }}>ราคา / หน่วย</th>
          <th style={{ border: GRID_LINE, padding: "8px 6px", textAlign: "center", width: 110 }}>ราคา</th>
        </tr>
      </thead>
      <tbody>{rowNodes}</tbody>
    </table>
  );

  const footer = (
    <div data-m="footer" style={{ display: "flow-root" }}>
      {/* ส่วนท้ายเป็นตารางต่อจากตารางรายการ: ช่องซ้าย = หมายเหตุ/ช่องทางชำระเงิน, ช่องขวา = ยอดรวมแต่ละบรรทัด
          คอลัมน์ป้าย 170px = จำนวน(70)+ราคา/หน่วย(100) และคอลัมน์ยอด 110px = ราคา จึงตรงแนวกับตารางรายการ */}
      {(() => {
        const totalRows = [
          { label: "ราคารวม", value: money(t.subtotal), show: true },
          { label: `ภาษีมูลค่าเพิ่ม ${doc.vat_percent || 0}%`, value: money(t.vat_amount), show: doc.vat_enabled },
          { label: `ภาษีหัก ณ ที่จ่าย ${doc.wht_percent || 0}%`, value: `${t.wht_amount ? "-" : ""}${money(t.wht_amount)}`, show: doc.wht_enabled },
        ].filter((r) => r.show);
        const leftBlock = isPurchaseOrder(doc.doc_type) ? (
          <>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>หมายเหตุ / เงื่อนไข</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{doc.note || ""}</div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>ช่องทางการชำระเงิน</div>
            <div><span style={{ fontWeight: 700 }}>ชื่อบัญชี:&nbsp;&nbsp;</span>{doc.payment_account_name || ""}</div>
            <div><span style={{ fontWeight: 700 }}>เลขบัญชี:&nbsp;&nbsp;</span>{doc.payment_account_no || ""}{doc.payment_bank ? ` (${doc.payment_bank})` : ""}</div>
          </>
        );
        const labelCell = { border: GRID_LINE, padding: "6px 10px", textAlign: "right", fontWeight: 700 };
        const valueCell = { border: GRID_LINE, padding: "6px 6px", textAlign: "right" };
        return (
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col />
              <col style={{ width: 170 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <tbody>
              {totalRows.map((r, i) => (
                <tr key={i}>
                  {i === 0 ? (
                    <td rowSpan={totalRows.length + 1} style={{ border: GRID_LINE, padding: "8px 10px", verticalAlign: "top" }}>{leftBlock}</td>
                  ) : null}
                  <td style={labelCell}>{r.label}</td>
                  <td style={valueCell}>{r.value}</td>
                </tr>
              ))}
              {/* ใส่สีพื้นที่ช่อง ไม่ใส่ที่ <tr>: html2canvas วาดพื้นหลังของแถวทับข้อความในช่องซ้ายที่ rowSpan มาถึงแถวนี้
                  ทำให้บรรทัดท้าย ๆ ของหมายเหตุหายไปใน PDF ทั้งที่ preview เห็นปกติ */}
              <tr>
                <td style={{ ...labelCell, background: "#f3f4f6" }}>ราคารวมทั้งหมด</td>
                <td style={{ ...valueCell, fontWeight: 700, background: "#f3f4f6" }}>{money(t.grand_total)}</td>
              </tr>
              <tr>
                <td colSpan={3} style={{ border: GRID_LINE, padding: "6px 10px", textAlign: "right", fontStyle: "italic", color: "#6b7280" }}>({t.amount_text})</td>
              </tr>
            </tbody>
          </table>
        );
      })()}

      <div style={{ display: "flex", alignItems: "flex-start", marginTop: 32 }}>
        {/* ใบเสนอราคา: ช่องลงนามของลูกค้าฝั่งซ้าย เว้นว่างให้เซ็นอนุมัติ และเว้นวันที่ให้เขียนเอง */}
        {doc.doc_type === "quotation" ? (
          <div style={{ width: 300, textAlign: "center" }}>
            <div style={{ height: 64, borderBottom: "1px dotted #94a3b8" }} />
            <div style={{ marginTop: 6 }}>( {doc.customer_name || "................................"} )</div>
            <div style={{ color: "#6b7280", marginTop: 2 }}>วันที่ ......../......../........</div>
          </div>
        ) : null}
        {/* ช่องลงนาม: เว้นที่ว่างด้านบนไว้เซ็นชื่อ (ถ้าเลือกพนักงานที่มีลายเซ็นในระบบ จะวางรูปลายเซ็นให้) ชื่อผู้ลงนามแสดงเฉพาะในวงเล็บ */}
        <div style={{ width: 300, marginLeft: "auto", textAlign: "center" }}>
          <div style={{ height: 64, borderBottom: "1px dotted #94a3b8", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            {doc.signer_signature_url ? <img src={doc.signer_signature_url} alt="" style={{ maxHeight: 62, maxWidth: 220, objectFit: "contain" }} /> : null}
          </div>
          <div style={{ marginTop: 6 }}>( {doc.signer_name || "................................"} )</div>
          <div style={{ color: "#6b7280", marginTop: 2 }}>{docDate(doc.doc_date)}</div>
        </div>
      </div>
    </div>
  );

  return { header, table, rows, footer, fontSize: 15, lineHeight: 1.35 };
}

// ---- มุมมองเอกสารจริง (read-only) แบ่งเป็นหน้า A4 อัตโนมัติ ใช้ทั้ง preview และ render เป็น PDF ----
function DocumentView({ doc, shadow }) {
  const parts = useMemo(() => (isVoucher(doc.doc_type) ? voucherParts(doc) : standardParts(doc)), [doc]);
  const { measureRef, pages } = usePaginatedDoc(parts, [doc]);
  const contHeader = <ContinuedHeader doc={doc} />;
  const list = pages || [{ first: true, rows: parts.rows.map((_, i) => i), footer: true }];

  return (
    <div style={{ width: A4_W, position: "relative" }}>
      {/* ชั้นวัดความสูงจริงของแต่ละส่วน ก่อนตัดหน้า (ไม่แสดงผล) */}
      <div ref={measureRef} aria-hidden="true" style={{ position: "absolute", left: -99999, top: 0, width: A4_W, padding: `0 ${PAGE_PAD}px`, boxSizing: "border-box", fontSize: parts.fontSize, lineHeight: parts.lineHeight, color: "#111", visibility: "hidden", pointerEvents: "none" }}>
        {parts.header}
        <div data-m="cont-header" style={{ display: "flow-root" }}>{contHeader}</div>
        {parts.table(parts.rows)}
        {parts.footer}
      </div>

      {list.map((p, idx) => (
        <div key={idx} style={{ marginTop: idx ? 16 : 0 }}>
          <A4Page fontSize={parts.fontSize} lineHeight={parts.lineHeight} index={idx} total={list.length} shadow={shadow}>
            {p.first ? parts.header : contHeader}
            {p.rows.length ? parts.table(p.rows.map((i) => parts.rows[i])) : null}
            {p.footer ? parts.footer : null}
          </A4Page>
        </div>
      ))}
    </div>
  );
}

export default function CompanyDocuments() {
  const [view, setView] = useState("list"); // list | editor
  const [list, setList] = useState([]);
  const [activeTab, setActiveTab] = useState("billing_note"); // แท็บของหน้า list ตามประเภทเอกสาร
  const [doc, setDoc] = useState(emptyDoc());
  const [selectedPartnerUid, setSelectedPartnerUid] = useState(""); // ลูกค้าที่เลือกจากทะเบียน
  const [linkedBillingUid, setLinkedBillingUid] = useState(""); // uid ใบวางบิลที่เลือกมาดึงข้อมูล (เฉพาะตอนออกใบเสร็จ)
  const [linkedTransactionUid, setLinkedTransactionUid] = useState(""); // uid ธุรกรรมต้นทาง (ตอนสร้างใบสำคัญรับ/จ่ายจากธุรกรรมการเงิน)
  const [previewDoc, setPreviewDoc] = useState(null); // doc ที่กำลัง preview
  const [pendingDownload, setPendingDownload] = useState(null); // doc ที่รอ render เป็น PDF
  const [bulkRenderDoc, setBulkRenderDoc] = useState(null); // doc ที่กำลัง render อยู่ตอนดาวน์โหลดทั้งหมด
  const [bulkProgress, setBulkProgress] = useState(null); // { current, total } ระหว่างดาวน์โหลด
  const [selectedUids, setSelectedUids] = useState([]); // เอกสารที่ติ๊กไว้ใน tab ปัจจุบัน (ว่าง = เอาทั้ง tab)
  const [actionMenu, setActionMenu] = useState(null); // เมนู "..." ของแต่ละแถว { x, y, item }
  const [partners, setPartners] = useState([]); // ทะเบียนลูกค้า/คู่ค้า ใช้เลือกแทนการพิมพ์ซ้ำ
  const [employees, setEmployees] = useState([]); // พนักงาน ใช้เลือกเป็นผู้รับเงิน/ผู้จ่ายเงิน พร้อมลายเซ็น
  const [company, setCompany] = useState(null); // ข้อมูลองค์กรจากตั้งค่าระบบ
  const [companyLoaded, setCompanyLoaded] = useState(false);
  // ---- filter (ใช้ร่วมกันทุก tab) ----
  const [searchInput, setSearchInput] = useState("");
  const [dateFromInput, setDateFromInput] = useState(() => currentMonthRange().date_from);
  const [dateToInput, setDateToInput] = useState(() => currentMonthRange().date_to);
  const [filters, setFilters] = useState(defaultFilters); // ค่าที่กดค้นหาแล้วเท่านั้น
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef(null);
  const bulkResolveRef = useRef(null); // resolve ของ promise ที่รอ printRef render doc ใบถัดไป
  const signatureCacheRef = useRef({}); // uid พนักงาน -> data url ลายเซ็น (ดึงซ้ำแค่ครั้งเดียวต่อรอบเปิดหน้า)
  const previewOuterRef = useRef(null); // กล่องครอบ preview (วัดความกว้าง)
  const previewInnerRef = useRef(null); // เอกสาร 794px จริง
  const [previewScale, setPreviewScale] = useState(1);

  const info = docTypeInfo(doc.doc_type);
  const payeeSigner = voucherSigners(doc.doc_type).find((sg) => sg.payee);
  const totals = useMemo(() => computeTotals(doc), [doc]);
  const printDoc = bulkRenderDoc || pendingDownload || previewDoc || doc;

  // ---- list ---- (ดึงมาทั้งหมดครั้งเดียว แล้วแยกตาม tab ฝั่ง client)
  const loadList = useCallback(async () => {
    loading(true);
    const res = await GET(API, { page: 0 });
    if (res?.status) setList(res.data || []);
    loading(false);
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // โหลดข้อมูลองค์กร (ตั้งค่าระบบ -> ข้อมูลองค์กร) ไว้ prefill ผู้ออกบิล
  useEffect(() => {
    (async () => {
      const res = await GET("v1/admin/companies");
      if (res?.status && res.data) setCompany(res.data);
      setCompanyLoaded(true);
    })();
  }, []);

  // ทะเบียนลูกค้า/คู่ค้า สำหรับเลือกเติมชื่อ เลขภาษี และที่อยู่อัตโนมัติ
  useEffect(() => {
    (async () => {
      const res = await GET("v1/admin/partners", { page: 0, is_active: true });
      if (res?.status) setPartners(res.data || []);
    })();
  }, []);

  // พนักงาน สำหรับเลือกเป็นผู้รับเงิน/ผู้จ่ายเงินของใบสำคัญ
  useEffect(() => {
    (async () => {
      const res = await GET("v1/admin/employees", { page: 0 });
      if (res?.status) setEmployees(res.data || []);
    })();
  }, []);

  // มาจากหน้าธุรกรรมการเงิน: เปิดเอกสารใหม่พร้อมข้อมูลตั้งต้น (รอข้อมูลองค์กรโหลดก่อน)
  useEffect(() => {
    const prefill = location.state?.createDoc;
    if (!prefill || !companyLoaded) return;
    startCreate(prefill.doc_type, prefill);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, companyLoaded]);

  // ย่อ preview ให้พอดีความกว้าง modal (ไม่มีสกรอลล์แนวนอน)
  useEffect(() => {
    if (!previewDoc) return undefined;
    const compute = () => {
      const outer = previewOuterRef.current;
      const inner = previewInnerRef.current;
      if (!outer || !inner || !outer.clientWidth) return; // ยัง animate อยู่ (กว้าง 0) ข้ามไปก่อน
      const s = Math.min(1, outer.clientWidth / 794);
      setPreviewScale(s);
      outer.style.height = `${inner.offsetHeight * s}px`;
    };
    compute();
    // เผื่อ modal ยัง fade-in / ฟอนต์โหลดเสร็จ คำนวณซ้ำหลายจังหวะ
    const timers = [80, 200, 350].map((ms) => setTimeout(compute, ms));
    window.addEventListener("resize", compute);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", compute);
    };
  }, [previewDoc]);

  // เอกสารที่ผ่าน filter (คำค้น + ช่วงวันที่) ใช้ร่วมกันทุก tab
  const filteredList = useMemo(() => {
    const q = filters.searchText.trim().toLowerCase();
    return list.filter((d) => {
      const date = String(d.doc_date || "").slice(0, 10);
      if (filters.date_from && (!date || date < filters.date_from)) return false;
      if (filters.date_to && (!date || date > filters.date_to)) return false;
      return matchSearch(d, q);
    });
  }, [list, filters]);

  // จำนวนเอกสารใน tab ที่ตรงคำค้น โดยไม่สนใจช่วงวันที่
  // ตอนตารางว่าง ตัวเลขนี้ = จำนวนที่ถูกช่วงวันที่ตัดออก ใช้บอกผู้ใช้ว่าหาไม่เจอเพราะวันที่ ไม่ใช่ไม่มีเอกสาร
  const searchMatchCount = useMemo(() => {
    const q = filters.searchText.trim().toLowerCase();
    return list.filter((d) => d.doc_type === activeTab && matchSearch(d, q)).length;
  }, [list, filters, activeTab]);

  const summary = useMemo(() => {
    const by = (v) => filteredList.filter((d) => d.doc_type === v).length;
    return DOC_TYPES.reduce((acc, d) => ({ ...acc, [d.value]: by(d.value) }), { total: filteredList.length });
  }, [filteredList]);

  // เอกสารของ tab ที่กำลังเปิดอยู่
  const visibleList = useMemo(() => filteredList.filter((d) => d.doc_type === activeTab), [filteredList, activeTab]);
  const activeInfo = docTypeInfo(activeTab);
  // สรุปของประเภทที่เลือก: จำนวน/ยอดสุทธิรวมตามเงื่อนไขที่ค้นหา และจำนวนทั้งหมดในระบบ (ไม่จำกัดวันที่)
  const typeSummary = useMemo(
    () => ({
      count: visibleList.length,
      total: visibleList.reduce((s, d) => s + (Number(d.grand_total) || 0), 0),
      all: list.filter((d) => d.doc_type === activeTab).length,
    }),
    [visibleList, list, activeTab]
  );

  // ---- เลือกเอกสารสำหรับดาวน์โหลด ----
  // นับเฉพาะที่ยังมองเห็นอยู่ ถ้าเปลี่ยน filter จนใบที่ติ๊กไว้หายไป จะไม่ถูกนำไปดาวน์โหลด
  const selectedDocs = useMemo(() => visibleList.filter((d) => selectedUids.includes(d.uid)), [visibleList, selectedUids]);
  const downloadCount = selectedDocs.length; // ต้องติ๊กเลือกก่อนถึงจะกดดาวน์โหลดได้
  const allVisibleChecked = visibleList.length > 0 && selectedDocs.length === visibleList.length;

  // เปลี่ยน tab แล้วเริ่มเลือกใหม่ ไม่ให้เอกสารต่างประเภทค้างอยู่ในรายการที่เลือก
  useEffect(() => {
    setSelectedUids([]);
  }, [activeTab]);

  const toggleDoc = (uid) => {
    setSelectedUids((current) => (current.includes(uid) ? current.filter((id) => id !== uid) : [...current, uid]));
  };

  const toggleAllVisible = () => {
    setSelectedUids(allVisibleChecked ? [] : visibleList.map((d) => d.uid));
  };

  // ---- เมนู "..." ของแต่ละแถว ----
  // ปิดเมื่อคลิกที่อื่น เลื่อนจอ หรือกด Esc
  useEffect(() => {
    if (!actionMenu) return undefined;

    const close = () => setActionMenu(null);
    const onKeyDown = (e) => e.key === "Escape" && setActionMenu(null);

    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [actionMenu]);

  // เปิดตรงตำแหน่งที่กด (position: fixed จึงไม่โดนตารางที่ scroll ได้บังไว้)
  const openActionMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const menuWidth = 220;
    const menuHeight = 220;
    setActionMenu({ x: Math.min(e.clientX, window.innerWidth - menuWidth), y: Math.min(e.clientY, window.innerHeight - menuHeight), item });
  };

  const handleSearch = () => setFilters({ searchText: searchInput, date_from: dateFromInput, date_to: dateToInput });

  // ปุ่มช่วงวันที่สำเร็จรูป: ตั้งค่า input แล้วค้นให้เลย ไม่ต้องกดค้นหาซ้ำ
  const applyRange = ({ date_from, date_to }) => {
    setDateFromInput(date_from);
    setDateToInput(date_to);
    setFilters({ searchText: searchInput, date_from, date_to });
  };

  const activeRangeKey = useMemo(() => {
    const hit = QUICK_RANGES.find((r) => {
      const v = r.range();
      return v.date_from === filters.date_from && v.date_to === filters.date_to;
    });
    return hit?.key || "";
  }, [filters]);

  // รีเซ็ตกลับเป็นค่าเริ่มต้น = ช่วงวันที่ของเดือนปัจจุบัน
  const handleResetFilters = () => {
    const d = defaultFilters();
    setSearchInput(d.searchText);
    setDateFromInput(d.date_from);
    setDateToInput(d.date_to);
    setFilters(d);
  };

  // ใบวางบิลทั้งหมด ไว้ให้เลือกดึงข้อมูลตอนออกใบเสร็จ
  const billingOptions = useMemo(
    () =>
      list
        .filter((d) => d.doc_type === "billing_note")
        .map((d) => ({ value: d.uid, label: `${d.doc_no || "(ยังไม่ออกเลขที่)"} - ${d.customer_name || "-"}`, raw: d })),
    [list]
  );

  // ---- ลายเซ็นพนักงาน (ผู้รับเงิน/ผู้จ่ายเงินของใบสำคัญ) ----
  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const name = [e.prefix_name, e.first_name, e.last_name].filter(Boolean).join(" ").trim() || e.nickname || e.employee_code || "-";
        return {
          value: e.uid,
          // บอกไปเลยว่าคนไหนยังไม่มีลายเซ็น จะได้ไม่ต้องเดาว่าทำไมเลือกแล้วไม่ขึ้นรูป
          label: `${name}${e.signature_name ? "" : " (ยังไม่มีลายเซ็น)"}`,
          raw: { ...e, display_name: name },
        };
      }),
    [employees]
  );
  // ผู้ลงนามเอกสารทั่วไป (ใบวางบิล/ใบเสนอราคา/ใบเสร็จ/ใบสั่งซื้อ) เลือกได้เฉพาะพนักงานที่บัญชีเป็น super user
  const superUserOptions = useMemo(() => employeeOptions.filter((o) => o.raw.user_type === "super_user"), [employeeOptions]);

  // ดึงลายเซ็นเป็น data url ผ่าน backend (ไม่ใช้ signed url ตรง ๆ เพราะ html2canvas จะติด CORS ตอนทำ PDF)
  const fetchSignature = useCallback(async (employeeUid) => {
    if (!employeeUid) return "";
    if (signatureCacheRef.current[employeeUid] !== undefined) return signatureCacheRef.current[employeeUid];
    const res = await GET(`v1/admin/employees/${employeeUid}/signature`);
    const url = (res?.status && res.data?.data_url) || "";
    // cache เฉพาะที่มีลายเซ็นจริง คนที่ยังไม่มีจะได้เห็นผลทันทีหลังไปอัปโหลดในเมนูพนักงาน
    if (url) signatureCacheRef.current[employeeUid] = url;
    return url;
  }, []);

  // เติมลายเซ็นเข้า doc ก่อนนำไปแสดง/แปลงเป็น PDF
  const withSignature = useCallback(
    async (d) => (d?.signer_employee_uid ? { ...d, signer_signature_url: await fetchSignature(d.signer_employee_uid) } : d),
    [fetchSignature]
  );

  // เลือกพนักงานเป็นผู้รับเงิน: เติมชื่อผู้ลงนามและลายเซ็นให้อัตโนมัติ
  const selectSignerEmployee = async (employeeUid) => {
    const opt = employeeOptions.find((o) => o.value === employeeUid);
    if (!opt) {
      setDoc((prev) => ({ ...prev, signer_employee_uid: "", signer_name: "", signer_signature_url: "" }));
      return;
    }
    setDoc((prev) => ({ ...prev, signer_employee_uid: employeeUid, signer_name: opt.raw.display_name, signer_signature_url: "" }));
    const url = await fetchSignature(employeeUid);
    setDoc((prev) => (prev.signer_employee_uid === employeeUid ? { ...prev, signer_signature_url: url } : prev));
  };

  // ใบสั่งซื้อ: เรียงผู้ขาย/ผู้รับจ้าง (vendor, both) ขึ้นก่อน เอกสารอื่นเรียงลูกค้าขึ้นก่อน
  const partnerOptions = useMemo(() => {
    const wantVendor = isPurchaseOrder(doc.doc_type);
    const rank = (item) => {
      const type = item.partner_type || "customer";
      if (type === "both") return 0;
      return (wantVendor ? type === "vendor" : type === "customer") ? 0 : 1;
    };
    return [...partners]
      .sort((a, b) => rank(a) - rank(b))
      .map((item) => ({
        value: item.uid,
        label: `${item.name}${item.tax_id ? ` (${item.tax_id})` : ""}`,
        raw: item,
      }));
  }, [partners, doc.doc_type]);

  // เลือกลูกค้าจากทะเบียน แล้วเติมชื่อ/เลขภาษี/ที่อยู่ให้อัตโนมัติ (ยังแก้ทับได้)
  const pickPartner = (value, item) => {
    setSelectedPartnerUid(value);
    if (!value || !item?.raw) return;

    const partner = item.raw;
    const addressWithBranch = [partner.address, partner.branch ? `(${partner.branch})` : ""].filter(Boolean).join(" ");

    setDoc((c) => ({
      ...c,
      customer_name: partner.name || c.customer_name,
      customer_tax_id: partner.tax_id || c.customer_tax_id,
      customer_address: addressWithBranch || c.customer_address,
    }));
  };

  // ---- navigation ----
  // prefill: ข้อมูลตั้งต้นจากที่อื่น (เช่น ธุรกรรมการเงิน) ไว้เติมลงในเอกสารใหม่
  const startCreate = (type, prefill) => {
    const base = emptyDoc(type);
    if (company) {
      base.company_name = company.name_th || "";
      base.company_tax_id = company.tax_number || "";
      base.company_address = composeAddress(company);
      base.payment_account_name = company.bank_account_name || "";
      base.payment_account_no = company.bank_account_number || "";
      base.payment_bank = company.bank_name || "";
    }
    if (prefill) {
      if (prefill.doc_date) base.doc_date = prefill.doc_date;
      if (prefill.customer_name) base.customer_name = prefill.customer_name;
      if (prefill.purpose) base.purpose = prefill.purpose;
      if (prefill.note) base.note = prefill.note;
      if (prefill.items?.length) base.items = prefill.items;
      if (prefill.wht_amount) base.wht_amount = Number(prefill.wht_amount) || 0;
    }
    setActiveTab(type);
    setDoc(base);
    setLinkedBillingUid("");
    setSelectedPartnerUid("");
    setLinkedTransactionUid(prefill?.transactionUid || "");
    setView("editor");
  };
  const openEdit = async (d) => {
    const mapped = mapApiDoc(d);
    setDoc(mapped);
    setLinkedBillingUid("");
    setSelectedPartnerUid("");
    setLinkedTransactionUid("");
    setView("editor");
    // ลายเซ็นไม่ได้เก็บใน DB ต้องดึงใหม่ตาม uid พนักงานที่บันทึกไว้
    if (mapped.signer_employee_uid) {
      const url = await fetchSignature(mapped.signer_employee_uid);
      setDoc((prev) => (prev.uid === mapped.uid ? { ...prev, signer_signature_url: url } : prev));
    }
  };
  // ทำซ้ำเอกสารที่กำลังแก้ไข: คัดลอกข้อมูลทั้งหมดเป็นเอกสารใหม่ (ล้าง uid + เลขที่เอกสารให้ออกใหม่อัตโนมัติ)
  const duplicateDoc = async () => {
    if (!(await alert("", "info", "ทำซ้ำเอกสาร", "คัดลอกข้อมูลทั้งหมดเป็นเอกสารใหม่? (ต้องกดบันทึกเพื่อสร้าง)", "ทำซ้ำ", "ยกเลิก"))) return;
    setDoc((c) => ({
      ...c,
      uid: "",
      doc_no: "",
      items: c.items.map((it) => ({ ...it })),
    }));
    setLinkedBillingUid("");
    setLinkedTransactionUid("");
    await alert("", "success", "สำเร็จ", "ทำซ้ำข้อมูลแล้ว กดบันทึกเพื่อสร้างเอกสารใบใหม่");
  };

  // ดึงข้อมูลจากใบวางบิลที่เลือกมาเติมในฟอร์มใบเสร็จ
  const pickBilling = (value, item) => {
    setLinkedBillingUid(value);
    if (!value || !item?.raw) return;
    const b = item.raw;
    setDoc((c) => ({
      ...c,
      customer_name: b.customer_name || "",
      customer_tax_id: b.customer_tax_id || "",
      customer_address: b.customer_address || "",
      items: (b.items && b.items.length ? b.items : [emptyItem()]).map((it) => ({
        name: it.name || "",
        detail: it.detail || "",
        qty: Number(it.qty) || 0,
        unit_price: Number(it.unit_price) || 0,
      })),
      vat_enabled: b.vat_enabled !== false,
      vat_percent: Number(b.vat_percent) || 0,
      wht_enabled: b.wht_enabled !== false,
      wht_percent: Number(b.wht_percent) || 0,
    }));
  };
  const backToList = () => {
    setView("list");
    loadList();
  };

  // ---- editor field setters ----
  const setField = (field, value) => setDoc((c) => ({ ...c, [field]: value }));
  const setItem = (idx, field, value) => setDoc((c) => ({ ...c, items: c.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)) }));
  const addItem = () => setDoc((c) => ({ ...c, items: [...c.items, emptyItem()] }));
  const removeItem = async (idx) => {
    if (doc.items.length === 1) return;
    if (!(await alert("", "warning", "ลบรายการ", "ต้องการลบรายการนี้?", "ลบ", "ยกเลิก"))) return;
    setDoc((c) => ({ ...c, items: c.items.filter((_, i) => i !== idx) }));
  };

  const buildPayload = () => {
    const t = computeTotals(doc);
    const voucher = isVoucher(doc.doc_type);
    return {
      doc_type: doc.doc_type,
      doc_no: doc.doc_no || undefined,
      doc_date: doc.doc_date,
      purpose: doc.purpose,
      company_name: doc.company_name,
      company_tax_id: doc.company_tax_id,
      company_address: doc.company_address,
      customer_name: doc.customer_name,
      customer_tax_id: doc.customer_tax_id,
      customer_address: doc.customer_address,
      items: voucher
        ? doc.items.map((it) => ({
            doc_date: it.doc_date,
            doc_no: doc.doc_no || undefined, // เลขที่เอกสารในแต่ละรายการ = เลขที่เอกสารหลักเสมอ
            description: it.description,
            amount: Number(it.amount) || 0,
          }))
        : doc.items.map((it) => ({
            name: it.name,
            detail: it.detail,
            qty: Number(it.qty) || 0,
            unit_price: Number(it.unit_price) || 0,
            amount: (Number(it.qty) || 0) * (Number(it.unit_price) || 0),
          })),
      subtotal: t.subtotal,
      vat_enabled: !voucher && !!doc.vat_enabled,
      vat_percent: Number(doc.vat_percent) || 0,
      vat_amount: t.vat_amount,
      wht_enabled: !!doc.wht_enabled,
      wht_percent: Number(doc.wht_percent) || 0,
      wht_amount: t.wht_amount,
      note: doc.note,
      grand_total: t.grand_total,
      amount_text: t.amount_text,
      payment_account_name: doc.payment_account_name,
      payment_account_no: doc.payment_account_no,
      payment_bank: doc.payment_bank,
      signer_name: doc.signer_name,
      signer_employee_uid: doc.signer_employee_uid || null,
      status: "issued",
    };
  };

  const saveDoc = async () => {
    if (!doc.customer_name.trim()) {
      await alert("", "warning", "แจ้งเตือน", `กรุณากรอกชื่อ${partyLabels(doc.doc_type).short}`);
      return;
    }
    if (!(await alert("", "info", "บันทึกเอกสาร", "ต้องการบันทึกเอกสารนี้?", "บันทึก", "ยกเลิก"))) return;
    const body = buildPayload();
    const isCreate = !doc.uid;
    loading(true);
    const res = isCreate ? await POST(API, body) : await PUT(`${API}/${doc.uid}`, body);
    // สร้างจากธุรกรรมการเงิน: บันทึกเลขที่เอกสารที่เพิ่งสร้าง กลับไปเป็นเลขอ้างอิงของธุรกรรมต้นทาง
    if (res?.status && isCreate && linkedTransactionUid && res.data?.doc_no) {
      await PUT(`v1/admin/finance-transactions/${linkedTransactionUid}/link-reference`, { reference_no: res.data.doc_no });
    }
    loading(false);
    if (res?.status) {
      setLinkedTransactionUid("");
      await alert("", "success", "สำเร็จ", "บันทึกเอกสารสำเร็จ");
      backToList();
    } else {
      await alert("", "warning", "แจ้งเตือน", res?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const deleteDoc = async (d) => {
    if (!(await alert("", "warning", "ลบเอกสาร", `ต้องการลบเอกสาร ${d.doc_no || ""} ?`, "ลบ", "ยกเลิก"))) return;
    loading(true);
    const res = await DELETE(`${API}/${d.uid}`, null);
    loading(false);
    if (res?.status) {
      await alert("", "success", "สำเร็จ", "ลบเอกสารสำเร็จ");
      loadList();
    } else {
      await alert("", "warning", "แจ้งเตือน", res?.message || "ลบไม่สำเร็จ");
    }
  };

  // ---- download PDF (render printRef เป็นรูป) ----
  useEffect(() => {
    if (!pendingDownload) return;
    const run = async () => {
      try {
        await new Promise((r) => setTimeout(r, 120)); // รอ DOM อัปเดต + คำนวณการแบ่งหน้า
        const nodes = Array.from(printRef.current.querySelectorAll("[data-a4page]"));
        if (!nodes.length) throw new Error("no page rendered");
        const pageW = 595.28; // A4 ที่ 72dpi
        const pageH = 841.89;
        const images = [];
        for (const node of nodes) {
          const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
          images.push(canvas.toDataURL("image/png"));
        }
        const di = docTypeInfo(pendingDownload.doc_type);
        pdfMake
          .createPdf({
            pageSize: { width: pageW, height: pageH },
            pageMargins: [0, 0, 0, 0],
            content: images.map((img, i) => ({ image: img, width: pageW, height: pageH, ...(i ? { pageBreak: "before" } : {}) })),
          })
          .download(`${di.th}_${pendingDownload.doc_no || "draft"}`);
      } catch (err) {
        await alert("", "warning", "แจ้งเตือน", "สร้าง PDF ไม่สำเร็จ");
      } finally {
        setPendingDownload(null);
      }
    };
    run();
  }, [pendingDownload]);

  const downloadDoc = async (d) => setPendingDownload(await withSignature(d.uid ? mapApiDoc(d) : d));

  // ---- download ทั้งหมดใน tab (รวมเป็น PDF ไฟล์เดียว เอกสาร 1 ใบ = 1 หน้า) ----
  // render ทีละใบผ่าน printRef เดิม: ตั้ง state -> รอ effect ปล่อย resolve หลัง browser paint -> ค่อย capture
  useEffect(() => {
    if (!bulkRenderDoc || !bulkResolveRef.current) return undefined;
    const resolve = bulkResolveRef.current;
    bulkResolveRef.current = null;
    const timer = setTimeout(resolve, 50);
    return () => clearTimeout(timer);
  }, [bulkRenderDoc]);

  const captureDoc = async (d) => {
    const prepared = await withSignature(mapApiDoc(d));
    await new Promise((resolve) => {
      bulkResolveRef.current = resolve;
      setBulkRenderDoc(prepared);
    });
    const canvas = await html2canvas(printRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
    return { image: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
  };

  // ดาวน์โหลดเป็น zip เดียว ข้างในแยกไฟล์ 1 เอกสาร = 1 ไฟล์ PDF
  // (ไฟล์ใน zip เหมือนกดปุ่ม PDF รายบรรทัดทุกประการ) ใช้ zip เพื่อไม่ต้องพึ่ง permission ดาวน์โหลดหลายไฟล์ของเบราว์เซอร์
  // ติ๊กเลือกไว้ = เอาเฉพาะที่ติ๊ก, ไม่ติ๊กเลย = เอาทั้ง tab ตามเงื่อนไขที่ค้นหา
  const downloadDocs = async () => {
    const di = docTypeInfo(activeTab);
    const targets = selectedDocs; // ดาวน์โหลดเฉพาะที่ติ๊กเลือกไว้เท่านั้น
    if (!targets.length) {
      await alert("", "warning", "แจ้งเตือน", `กรุณาติ๊กเลือก${di.th}ที่ต้องการดาวน์โหลดก่อน`);
      return;
    }
    const total = targets.length;
    const confirmed = await alert(
      "",
      "info",
      "ดาวน์โหลดเอกสาร",
      `จะรวม${di.th} ${total} ไฟล์ที่เลือกไว้ (1 เอกสาร = 1 ไฟล์ PDF) เป็นไฟล์ zip เดียว`,
      "ดาวน์โหลด",
      "ยกเลิก"
    );
    if (!confirmed) return;

    let failed = true;
    setBulkProgress({ current: 0, total });
    try {
      const zip = new JSZip();
      const used = {}; // กันชื่อไฟล์ชนกันตอนเลขที่เอกสารซ้ำหรือว่าง
      for (let i = 0; i < total; i += 1) {
        const d = targets[i];
        setBulkProgress({ current: i + 1, total });
        // ต้องทำทีละใบตามลำดับ เพราะใช้ printRef ตัวเดียวร่วมกัน
        // eslint-disable-next-line no-await-in-loop
        const { image, width, height } = await captureDoc(d);
        const pageW = 595.28;
        const imgH = (height * pageW) / width;
        // pdfmake 0.3 คืน Promise ไม่รับ callback ห้ามห่อด้วย new Promise ไม่งั้นค้างรอ resolve ที่ไม่มีวันมา
        // eslint-disable-next-line no-await-in-loop
        const blob = await pdfMake
          .createPdf({ pageSize: { width: pageW, height: imgH }, pageMargins: [0, 0, 0, 0], content: [{ image, width: pageW, height: imgH }] })
          .getBlob();
        const base = `${di.th}_${d.doc_no || "draft"}`;
        used[base] = (used[base] || 0) + 1;
        zip.file(`${base}${used[base] > 1 ? `_${used[base]}` : ""}.pdf`, blob);
      }
      const range = filters.date_from || filters.date_to ? `_${filters.date_from || "all"}_${filters.date_to || "all"}` : "";
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, selectedDocs.length ? `${di.th}_ที่เลือก_${total}ไฟล์.zip` : `${di.th}_ทั้งหมด${range}.zip`);
      failed = false;
    } catch (err) {
      console.error("Download all documents error:", err); // แจ้งเตือนผู้ใช้หลังปิด progress ด้านล่าง
    } finally {
      bulkResolveRef.current = null;
      setBulkRenderDoc(null);
      setBulkProgress(null);
    }
    if (failed) await alert("", "warning", "แจ้งเตือน", "สร้าง PDF ไม่สำเร็จ");
  };

  // ---- inline input (editor) ----
  const inlineInput = (value, onChange, opts = {}) => (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={opts.placeholder || ""}
      type={opts.type || "text"}
      style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "1px 6px", outline: "none", background: "#fff", fontSize: opts.fontSize || 14, fontWeight: opts.bold ? 700 : 400, textAlign: opts.align || "left", width: opts.width || "100%", ...opts.style }}
      onFocus={(e) => (e.target.style.borderColor = "#0f6bdc")}
      onBlur={(e) => (e.target.style.borderColor = "#cbd5e1")}
    />
  );

  return (
    <AppShell title="เอกสารการเงิน" description="สร้างและจัดการเอกสารการเงิน เช่น ใบเสนอราคา / ใบวางบิล / ใบกำกับภาษี / ใบเสร็จ / ใบสำคัญรับ / ใบสั่งซื้อ / ใบสำคัญจ่าย">
      {view === "list" ? (
        <div className="row g-3">
          {/* แถบเลือกประเภทเอกสาร: จอใหญ่เป็นคอลัมน์ซ้ายแบ่งกลุ่มฝั่งขาย/ฝั่งซื้อ จอเล็กยุบเป็นปุ่มเลื่อนแนวนอนแถวเดียว */}
          <div className="col-12 col-lg-3 col-xxl-2">
            <div className="rounded-4 bg-white h-100 p-2 p-lg-3" style={PANEL_STYLE}>
              <div className="d-none d-lg-block">
                <div className="d-flex align-items-center justify-content-between px-2 mb-2">
                  <span className="fw-bold" style={{ color: "#22384a" }}>ประเภทเอกสาร</span>
                  <span className="badge rounded-pill" style={TAB_BADGE.idle} title="เอกสารทุกประเภทตามเงื่อนไขที่ค้นหา">{summary.total}</span>
                </div>
                {DOC_GROUPS.map((g) => (
                  <div key={g.key} className="mb-3">
                    <div className="text-secondary fw-semibold px-2 mb-1" style={{ fontSize: 11, letterSpacing: 0.3 }}>{g.label}</div>
                    {g.types.map((v) => {
                      const d = docTypeInfo(v);
                      const active = activeTab === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          className={`btn ${active ? "btn-primary" : ""} w-100 d-flex align-items-center gap-2 text-start fw-semibold mb-1 px-2`}
                          style={active ? undefined : NAV_IDLE_STYLE}
                          onClick={() => setActiveTab(v)}
                        >
                          {/* แท็บที่เลือกอยู่พื้นน้ำเงิน ไอคอนต้องขาว ไม่งั้นสีประเภทจะจมไปกับพื้น */}
                          <i className={d.icon} style={{ width: 18, textAlign: "center", ...(active ? {} : { color: d.color }) }} />
                          <span className="flex-grow-1 text-truncate">{d.th}</span>
                          <span className="badge rounded-pill" style={active ? TAB_BADGE.active : TAB_BADGE.idle}>{summary[v] ?? 0}</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="d-lg-none d-flex gap-2 flex-wrap scroll-x-mobile">
                {DOC_GROUPS.flatMap((g) => g.types).map((v) => {
                  const d = docTypeInfo(v);
                  const active = activeTab === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      className={`btn btn-sm ${active ? "btn-primary" : ""} d-flex align-items-center gap-2 fw-semibold text-nowrap`}
                      style={active ? undefined : TAB_IDLE_STYLE}
                      onClick={() => setActiveTab(v)}
                    >
                      <i className={d.icon} style={active ? undefined : { color: d.color }} /> {d.th}
                      <span className="badge rounded-pill" style={active ? TAB_BADGE.active : TAB_BADGE.idle}>{summary[v] ?? 0}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-9 col-xxl-10">
            {/* สรุปเฉพาะประเภทที่เลือก (ตามช่วงวันที่/คำค้น) ไม่ต้องไล่การ์ดทุกประเภทอีก */}
            <div className="row g-3 mb-3">
              {[
                { label: `${activeInfo.th}ในช่วงที่เลือก`, value: typeSummary.count, unit: "ใบ", icon: activeInfo.icon, color: activeInfo.color },
                { label: `ยอดสุทธิรวม (${activeInfo.th})`, value: money(typeSummary.total), unit: "บาท", icon: "fa-solid fa-coins", color: "#10b981" },
                { label: `${activeInfo.th}ทั้งหมดในระบบ`, value: typeSummary.all, unit: "ใบ", icon: "fa-solid fa-folder-open", color: BRAND_COLOR },
              ].map((c) => (
                <div className="col-12 col-sm-4" key={c.label}>
                  <div className="h-100 rounded-4 p-3 d-flex align-items-center gap-3" style={SUMMARY_CARD_STYLE}>
                    <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={summaryIconStyle(c.color)}><i className={c.icon} /></div>
                    <div className="min-w-0">
                      <div className="text-secondary text-truncate" style={{ fontSize: 12 }}>{c.label}</div>
                      <div className="d-flex align-items-baseline gap-1">
                        <span className="h4 mb-0 fw-bold" style={{ color: BRAND_COLOR }}>{c.value}</span>
                        <span className="text-secondary small">{c.unit}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* filter: ค้นหา + ช่วงวันที่ ใช้ร่วมกันทุกประเภท (ค่าเริ่มต้น = เดือนปัจจุบัน) */}
            <div className="rounded-4 p-3 mb-3 bg-white" style={PANEL_STYLE}>
              <div className="d-flex gap-2 flex-wrap align-items-center filters-mobile-stack">
                <input
                  type="text"
                  className="form-control"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="ค้นหาเลขที่ / ลูกค้า / รายละเอียด..."
                  style={{ width: "260px", borderRadius: "12px" }}
                />
                <DateInput
                  className="form-control"
                  value={dateFromInput}
                  onChange={(e) => setDateFromInput(e.target.value)}
                  title="ตั้งแต่วันที่"
                  style={{ width: "160px", borderRadius: "12px" }}
                />
                <span className="text-secondary">ถึง</span>
                <DateInput
                  className="form-control"
                  value={dateToInput}
                  onChange={(e) => setDateToInput(e.target.value)}
                  title="ถึงวันที่"
                  style={{ width: "160px", borderRadius: "12px" }}
                />
                <button type="button" className="btn btn-primary px-4" onClick={handleSearch} style={{ borderRadius: "12px" }}>
                  <i className="fa-solid fa-magnifying-glass me-2" />ค้นหา
                </button>
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={handleResetFilters}
                  style={{ borderRadius: "12px", border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}
                >
                  <i className="fa-solid fa-rotate-left me-2" />รีเซ็ต
                </button>
              </div>

              {/* ช่วงวันที่สำเร็จรูป + สรุปผลลัพธ์ ให้เห็นชัดว่าช่วงวันที่กำลังจำกัดผลค้นหาอยู่ */}
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-3" style={{ borderTop: "1px solid rgba(18,125,255,0.08)" }}>
                {QUICK_RANGES.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    className={`btn btn-sm ${activeRangeKey === r.key ? "btn-primary" : ""} fw-semibold`}
                    style={activeRangeKey === r.key ? { borderRadius: 10 } : { ...TAB_IDLE_STYLE, borderRadius: 10 }}
                    onClick={() => applyRange(r.range())}
                  >
                    {r.label}
                  </button>
                ))}
                <span className="text-secondary small ms-auto">
                  แสดง <strong style={{ color: BRAND_COLOR }}>{visibleList.length}</strong> จาก {typeSummary.all} {activeInfo.th}
                  {filters.date_from || filters.date_to
                    ? ` · ${filters.date_from ? docDate(filters.date_from) : "เริ่มต้น"} - ${filters.date_to ? docDate(filters.date_to) : "ปัจจุบัน"}`
                    : " · ทุกช่วงวันที่"}
                </span>
              </div>
            </div>

            {/* ตารางของประเภทที่เลือก: หัวตารางบอกประเภท + ปุ่มสร้าง/ดาวน์โหลดอยู่ที่เดียวกัน */}
            <div className="rounded-4 p-3 bg-white" style={PANEL_STYLE}>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={summaryIconStyle(activeInfo.color)}><i className={activeInfo.icon} /></span>
                <div className="min-w-0">
                  <div className="fw-bold" style={{ color: "#22384a" }}>{activeInfo.th}</div>
                  <div className="text-secondary" style={{ fontSize: 11, letterSpacing: 0.5 }}>{activeInfo.en}</div>
                </div>
                <div className="ms-auto d-flex gap-2 flex-wrap flex-shrink-0">
                  {/* ต้องติ๊กเลือกก่อน ไม่เลือก = ปุ่มเทา กดไม่ได้ */}
                  <button
                    className="btn text-white px-3 fw-semibold"
                    style={downloadCount && !bulkProgress ? { background: "#dc2626" } : { background: "#9ca3af", cursor: "not-allowed", opacity: 1 }}
                    disabled={!downloadCount || !!bulkProgress}
                    aria-label="ดาวน์โหลด"
                    title={
                      bulkProgress
                        ? `กำลังสร้าง ${bulkProgress.current}/${bulkProgress.total}`
                        : downloadCount
                          ? `ดาวน์โหลด${activeInfo.th} ${downloadCount} ใบที่เลือกไว้ เป็นไฟล์ zip แยกไฟล์ละ 1 เอกสาร`
                          : "ติ๊กเลือกเอกสารที่ต้องการก่อน แล้วจึงกดดาวน์โหลด"
                    }
                    onClick={downloadDocs}
                  >
                    {bulkProgress ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin me-2" />{bulkProgress.current}/{bulkProgress.total}
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-download me-2" />PDF ({downloadCount})
                      </>
                    )}
                  </button>
                  <button className="btn btn-primary px-4 fw-semibold" onClick={() => startCreate(activeTab)}>
                    <i className="fa-solid fa-plus me-2" />สร้าง{activeInfo.th}
                  </button>
                </div>
              </div>

              <div className="table-responsive table-scroll-mobile">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center" style={{ width: 44 }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={allVisibleChecked}
                          disabled={!visibleList.length}
                          onChange={toggleAllVisible}
                          title="เลือกทั้งหมดในประเภทนี้"
                        />
                      </th>
                      <th className="text-secondary fw-medium" style={{ width: 50 }}>No.</th>
                      <th className="text-secondary fw-medium">เลขที่</th>
                      <th className="text-secondary fw-medium">{listPartyHeader(activeTab)}</th>
                      <th className="text-secondary fw-medium">คำอธิบาย</th>
                      <th className="text-secondary fw-medium">วันที่</th>
                      <th className="text-secondary fw-medium text-end text-nowrap">ยอดสุทธิ</th>
                      <th className="text-secondary fw-medium text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleList.length === 0 ? (
                      <tr><td colSpan={8} className="text-center text-secondary py-4">
                        {searchMatchCount > 0 ? (
                          <>
                            {/* มีเอกสารตรงคำค้น แต่ถูกช่วงวันที่ตัดออก — บอกให้ชัดพร้อมปุ่มดูข้ามช่วงวันที่ */}
                            พบ {searchMatchCount} {activeInfo.th} ที่ตรงคำค้น แต่อยู่นอกช่วงวันที่ที่เลือก
                            <button
                              type="button"
                              className="btn btn-sm btn-primary fw-semibold ms-2"
                              style={{ borderRadius: 10 }}
                              onClick={() => applyRange({ date_from: "", date_to: "" })}
                            >
                              ค้นหาทุกช่วงวันที่
                            </button>
                          </>
                        ) : typeSummary.all > 0 ? (
                          `ไม่พบ${activeInfo.th}ตามเงื่อนไขที่ค้นหา — ลองปรับคำค้นหรือช่วงวันที่ แล้วกดค้นหาอีกครั้ง`
                        ) : (
                          `ยังไม่มี${activeInfo.th} — กด "สร้าง${activeInfo.th}" เพื่อเริ่ม`
                        )}
                      </td></tr>
                    ) : (
                      visibleList.map((d, i) => (
                        <tr key={d.uid}>
                          <td data-label="เลือก" className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedUids.includes(d.uid)}
                              onChange={() => toggleDoc(d.uid)}
                              title="เลือกเอกสารนี้เพื่อดาวน์โหลด"
                            />
                          </td>
                          <td data-label="No.">{i + 1}</td>
                          <td data-label="เลขที่" className="fw-semibold text-nowrap">
                            <i className={`${activeInfo.icon} me-2`} style={{ color: activeInfo.color }} />{d.doc_no}
                          </td>
                          <td data-label={listPartyHeader(activeTab)}>{d.customer_name || "-"}</td>
                          <td data-label="คำอธิบาย">
                            {d.purpose || d.note ? (
                              <span className="d-inline-block text-truncate align-middle" style={{ maxWidth: 240 }} title={d.purpose || d.note}>
                                {d.purpose || d.note}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td data-label="วันที่" className="text-nowrap">{format_date(d.doc_date, "dd/mm/yyyy")}</td>
                          <td data-label="ยอดสุทธิ" className="text-end fw-semibold">{money(d.grand_total)}</td>
                          <td data-label="จัดการ" className="text-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary rounded-3"
                              title="เมนูจัดการ"
                              onClick={(e) => openActionMenu(e, d)}
                            >
                              <i className="fa-solid fa-ellipsis" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* editor toolbar */}
          <div className="rounded-3 p-3 mb-3 d-flex flex-wrap align-items-center gap-2" style={{ background: "#fff", border: "1px solid #dbe4f0", boxShadow: "0 4px 14px rgba(15,23,42,0.05)" }}>
            <button className="btn btn-sm btn-light fw-semibold" onClick={backToList}><i className="fa-solid fa-arrow-left me-1" /> กลับ</button>
            <span className="fw-bold ms-1" style={{ color: info.color }}><i className={`${info.icon} me-1`} />{doc.uid ? "แก้ไข" : "สร้าง"}{info.th}</span>
            <div className="ms-auto d-flex gap-2">
              {doc.uid ? (
                <button className="btn btn-sm btn-outline-primary fw-semibold" onClick={duplicateDoc}><i className="fa-solid fa-copy me-1" /> ทำซ้ำ</button>
              ) : null}
              <button className="btn btn-sm btn-outline-secondary fw-semibold" onClick={() => setPreviewDoc(doc)}><i className="fa-solid fa-eye me-1" /> Preview</button>
              <button className="btn btn-sm btn-primary fw-semibold" onClick={saveDoc}><i className="fa-solid fa-floppy-disk me-1" /> บันทึก</button>
              <button className="btn btn-sm fw-semibold text-white" style={{ background: "#dc2626" }} onClick={() => downloadDoc(doc)}><i className="fa-solid fa-file-pdf me-1" /> ดาวน์โหลด PDF</button>
            </div>
          </div>

          {/* editable A4 */}
          {isVoucher(doc.doc_type) ? (
            <div className="mx-auto" style={{ maxWidth: 820 }}>
              <div className="p-4 p-md-5" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, boxShadow: "0 8px 28px rgba(15,23,42,0.10)", color: "#1f2937" }}>
                {/* ข้อมูลบริษัท (หัวเอกสารกึ่งกลาง) */}
                <div className="text-center">
                  <img src={companyLogo} alt="" style={{ ...LOGO_STYLE, height: 56, marginBottom: 6 }} />
                  {inlineInput(doc.company_name, (v) => setField("company_name", v), { placeholder: "ชื่อบริษัท", align: "center", bold: true, fontSize: 20, width: "100%" })}
                  <div className="mt-1">
                    <textarea value={doc.company_address} onChange={(e) => setField("company_address", e.target.value)} placeholder="ที่อยู่บริษัท" rows={1}
                      style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "2px 6px", outline: "none", width: "100%", fontSize: 14, textAlign: "center", resize: "vertical" }} />
                  </div>
                  <div className="d-flex justify-content-center align-items-center gap-1 mt-1 codoc-field-row">
                    <span className="small">เลขประจำตัวผู้เสียภาษี</span>
                    {inlineInput(doc.company_tax_id, (v) => setField("company_tax_id", v), { placeholder: "เลขผู้เสียภาษี", align: "center", width: 200 })}
                  </div>
                </div>

                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-end gap-2 mt-3 pt-2" style={{ borderTop: "1px solid #cbd5e1" }}>
                  <div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: info.color, lineHeight: 1 }}>{info.th}</div>
                    <div style={{ fontSize: 15, color: "#6b7280" }}>{info.en}</div>
                  </div>
                  <div className="text-end w-100" style={{ maxWidth: 260 }}>
                    <div className="d-flex align-items-center justify-content-end gap-1 mb-1">
                      <span className="fw-bold">เลขที่:</span>
                      {inlineInput(doc.doc_no, (v) => setField("doc_no", v), { placeholder: "อัตโนมัติ", align: "right", bold: true, width: 180 })}
                    </div>
                    <div className="d-flex align-items-center justify-content-end gap-1">
                      <span className="fw-bold">วันที่:</span>
                      {inlineInput(doc.doc_date, (v) => setField("doc_date", v), { type: "date", width: 150 })}
                    </div>
                    <div className="small text-secondary mt-1">{docDate(doc.doc_date)}</div>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="d-flex align-items-center gap-2 mb-2 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 70 }}>ทะเบียน:</span>
                    <div className="flex-grow-1">
                      <Select
                        className="form-control"
                        value={selectedPartnerUid}
                        placeholder="-- เลือกจากทะเบียนลูกค้า/คู่ค้า (ไม่บังคับ) --"
                        options={partnerOptions}
                        onChange={pickPartner}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-1 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 70 }}>{voucherPartyLabel(doc.doc_type)}:</span>
                    {inlineInput(doc.customer_name, (v) => setField("customer_name", v), { placeholder: "ชื่อบุคคล/บริษัท" })}
                  </div>
                  <div className="d-flex align-items-center gap-2 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 70 }}>เพื่อชำระ:</span>
                    {inlineInput(doc.purpose, (v) => setField("purpose", v), { placeholder: "รายละเอียดวัตถุประสงค์การจ่าย/รับเงิน" })}
                  </div>
                </div>

                {/* ตารางรายการมีคอลัมน์ความกว้างคงที่รวมเกิน 500px — ห่อให้เลื่อนแนวนอนแทนที่จะบีบจนอ่านไม่ออกบนมือถือ */}
                <div className="table-responsive">
                <table className="w-100 mt-3" style={{ borderCollapse: "collapse", minWidth: 560 }}>
                  <thead>
                    <tr style={{ background: "#6b7280", color: "#fff" }}>
                      <th style={{ padding: "8px 6px", textAlign: "center", width: 110 }}>วันที่เอกสาร</th>
                      <th style={{ padding: "8px 6px", textAlign: "center", width: 130 }}>เลขที่เอกสาร</th>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>รายการ / Description</th>
                      <th style={{ padding: "8px 6px", textAlign: "center", width: 110 }}>จำนวนเงิน</th>
                      <th style={{ width: 34 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.items.map((it, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #eef2f7", verticalAlign: "top" }}>
                        <td className="align-top" style={{ padding: "8px 6px" }}>{inlineInput(it.doc_date, (v) => setItem(idx, "doc_date", v), { type: "date", align: "center" })}</td>
                        <td className="align-top text-center text-secondary" style={{ padding: "8px 6px" }}>{doc.doc_no || "อัตโนมัติ"}</td>
                        <td className="align-top" style={{ padding: "8px 10px" }}>
                          <textarea
                            value={it.description}
                            onChange={(e) => setItem(idx, "description", e.target.value)}
                            placeholder="รายการ / Description"
                            rows={Math.max(1, (it.description || "").split("\n").length)}
                            style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "2px 6px", outline: "none", width: "100%", fontSize: 14, whiteSpace: "pre-wrap", resize: "vertical" }}
                            onFocus={(e) => (e.target.style.borderColor = "#0f6bdc")}
                            onBlur={(e) => (e.target.style.borderColor = "#cbd5e1")}
                          />
                        </td>
                        <td className="align-top" style={{ padding: "8px 6px" }}>{inlineInput(it.amount, (v) => setItem(idx, "amount", v), { type: "number", align: "right" })}</td>
                        <td className="align-top" style={{ padding: "8px 2px", textAlign: "center" }}>
                          <button className="btn btn-sm btn-link text-danger p-0" title="ลบรายการ" onClick={() => removeItem(idx)} disabled={doc.items.length === 1}><i className="fa-solid fa-xmark" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <button className="btn btn-sm btn-outline-primary mt-2" onClick={addItem}><i className="fa-solid fa-plus me-1" /> เพิ่มรายการ</button>

                <div className="row mt-4 pt-3" style={{ borderTop: "1px solid #cbd5e1" }}>
                  <div className="col-12 col-sm-6">
                    <div className="d-flex align-items-start gap-2 mb-2">
                      <span className="fw-bold flex-shrink-0" style={{ width: 70 }}>หมายเหตุ:</span>
                      <textarea value={doc.note} onChange={(e) => setField("note", e.target.value)} placeholder="หมายเหตุ" rows={2}
                        style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "2px 6px", outline: "none", width: "100%", fontSize: 14, resize: "vertical" }} />
                    </div>
                    <div className="text-secondary fst-italic" style={{ whiteSpace: "nowrap" }}>จำนวนเงิน: ({totals.amount_text})</div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="d-flex justify-content-end align-items-baseline gap-3"><span className="fw-bold">จำนวนเงินรวม</span><span style={{ width: 130, textAlign: "right" }}>{money(totals.subtotal)} บาท</span></div>
                    <div className="d-flex justify-content-end align-items-center gap-3 mt-1">
                      <span className="fw-bold">หัก ณ ที่จ่าย</span>
                      {inlineInput(doc.wht_amount, (v) => setField("wht_amount", v), { type: "number", align: "right", width: 130 })}
                    </div>
                    <div className="d-flex justify-content-end align-items-baseline gap-3 mt-1 pt-1" style={{ borderTop: "1px solid #cbd5e1" }}>
                      <span className="fw-bold" style={{ color: "#dc2626" }}>คงเหลือสุทธิ</span>
                      <span className="fw-bold" style={{ width: 130, textAlign: "right", color: "#dc2626" }}>{money(totals.grand_total)} บาท</span>
                    </div>
                  </div>
                </div>

                {/* เลือกผู้รับเงิน/ผู้จ่ายเงินจากพนักงาน เพื่อดึงลายเซ็นมาแสดงบนเอกสาร */}
                <div className="mt-5 d-flex flex-wrap align-items-center gap-2 codoc-field-row">
                  <span className="small text-secondary">{payeeSigner.label} :</span>
                  <div style={{ minWidth: 260 }}>
                    <Select
                      className="form-control"
                      value={doc.signer_employee_uid}
                      options={employeeOptions}
                      placeholder="เลือกพนักงาน (ใช้ลายเซ็นในระบบ)"
                      onChange={(v) => selectSignerEmployee(v)}
                    />
                  </div>
                  {doc.signer_employee_uid ? (
                    <button type="button" className="btn btn-sm btn-light" onClick={() => selectSignerEmployee("")}>
                      <i className="fa-solid fa-xmark me-1" />ล้าง
                    </button>
                  ) : null}
                  {doc.signer_employee_uid && !doc.signer_signature_url ? (
                    <span className="small text-danger">พนักงานคนนี้ยังไม่มีลายเซ็นในระบบ (จะแสดงเฉพาะชื่อ)</span>
                  ) : null}
                </div>

                {/* ลายเซ็นผู้เกี่ยวข้อง */}
                <div className="d-flex justify-content-between mt-3 text-center">
                  {voucherSigners(doc.doc_type).map((s, i) => (
                    <div key={i} style={{ width: s.payee ? "29%" : "22%" }}>
                      <div style={{ borderBottom: "1px dotted #94a3b8", height: 72, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                        {s.payee && doc.signer_signature_url ? (
                          <img src={doc.signer_signature_url} alt="ลายเซ็น" style={{ maxHeight: 70, maxWidth: "100%", objectFit: "contain" }} />
                        ) : null}
                      </div>
                      <div className="mt-1 small text-secondary">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mx-auto" style={{ maxWidth: 820 }}>
              <div className="p-4 p-md-5" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, boxShadow: "0 8px 28px rgba(15,23,42,0.10)", color: "#1f2937" }}>
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2">
                  <div className="d-flex align-items-center gap-3">
                    <img src={companyLogo} alt="" style={LOGO_STYLE} />
                    <div>
                      <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>{info.th}</div>
                      <div style={{ fontSize: 18, color: "#6b7280" }}>{info.en}</div>
                    </div>
                  </div>
                  <div className="text-end w-100" style={{ maxWidth: 340 }}>
                    <div className="d-flex align-items-center justify-content-end gap-1 mb-1">
                      {inlineInput(doc.doc_no, (v) => setField("doc_no", v), { placeholder: "อัตโนมัติ", align: "right", bold: true, width: 200 })}
                    </div>
                    <div className="d-flex align-items-center justify-content-end gap-1">
                      <span className="fw-bold">วันที่:</span>
                      {inlineInput(doc.doc_date, (v) => setField("doc_date", v), { type: "date", width: 150 })}
                    </div>
                    <div className="small text-secondary mt-1">{docDate(doc.doc_date)}</div>
                  </div>
                </div>

                {/* ผู้ออกบิล / ผู้สั่งซื้อ (ดึงจากตั้งค่า > ข้อมูลองค์กร แก้ไขได้) */}
                <div className="mt-4">
                  <div className="d-flex align-items-center gap-2 mb-1 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 150 }}>{partyLabels(doc.doc_type).issuer}:</span>
                    {inlineInput(doc.company_name, (v) => setField("company_name", v), { placeholder: `ชื่อบริษัท${partyLabels(doc.doc_type).issuer}`, width: 420 })}
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-1 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 150 }}>เลขประจำตัวผู้เสียภาษี:</span>
                    {inlineInput(doc.company_tax_id, (v) => setField("company_tax_id", v), { placeholder: "เลขผู้เสียภาษี", width: 220 })}
                  </div>
                  <div className="d-flex align-items-start gap-2 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 150 }}>ที่อยู่:</span>
                    <textarea value={doc.company_address} onChange={(e) => setField("company_address", e.target.value)} placeholder={`ที่อยู่${partyLabels(doc.doc_type).issuer}`} rows={2}
                      style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "2px 6px", outline: "none", width: "100%", fontSize: 14, resize: "vertical" }} />
                  </div>
                </div>

                {/* ดึงข้อมูลจากใบวางบิล (เฉพาะตอนออกใบเสร็จรับเงิน / ใบกำกับภาษี) */}
                {canPullFromBilling(doc.doc_type) && (
                  <div className="mt-3 p-3" style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 8 }}>
                    <label className="fw-semibold mb-1 d-block" style={{ fontSize: 13 }}>
                      <i className="fa-solid fa-file-invoice-dollar me-1" /> เลือกข้อมูลจากใบวางบิล (ไม่บังคับ)
                    </label>
                    <Select
                      className="form-control"
                      value={linkedBillingUid}
                      placeholder="-- เลือกใบวางบิลเพื่อดึงข้อมูลลูกค้า/รายการ/ยอดเงินอัตโนมัติ --"
                      options={billingOptions}
                      onChange={pickBilling}
                    />
                  </div>
                )}

                {/* ลูกค้า/ผู้รับบริการ */}
                <div className="mt-3 pt-3" style={{ borderTop: "1px solid #cbd5e1" }}>
                  {/* เลือกจากทะเบียนลูกค้าเพื่อเติมชื่อ/เลขภาษี/ที่อยู่ให้อัตโนมัติ */}
                  <div className="d-flex align-items-center gap-2 mb-2 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 150 }}>เลือกจากทะเบียน:</span>
                    <div style={{ width: 420 }}>
                      <Select
                        className="form-control"
                        value={selectedPartnerUid}
                        placeholder={partyLabels(doc.doc_type).pick}
                        options={partnerOptions}
                        onChange={pickPartner}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-1 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 150 }}>{partyLabels(doc.doc_type).party}:</span>
                    {inlineInput(doc.customer_name, (v) => setField("customer_name", v), { placeholder: `ชื่อ${partyLabels(doc.doc_type).short}/บริษัท`, width: 420 })}
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-1 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 150 }}>เลขประจำตัวผู้เสียภาษี:</span>
                    {inlineInput(doc.customer_tax_id, (v) => setField("customer_tax_id", v), { placeholder: "เลขผู้เสียภาษี", width: 220 })}
                  </div>
                  <div className="d-flex align-items-start gap-2 codoc-field-row">
                    <span className="fw-bold flex-shrink-0" style={{ width: 150 }}>ที่อยู่:</span>
                    <textarea value={doc.customer_address} onChange={(e) => setField("customer_address", e.target.value)} placeholder={`ที่อยู่${partyLabels(doc.doc_type).short}`} rows={2}
                      style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "2px 6px", outline: "none", width: "100%", fontSize: 14, resize: "vertical" }} />
                  </div>
                </div>

                {/* ตารางรายการมีคอลัมน์ความกว้างคงที่รวมเกิน 500px — ห่อให้เลื่อนแนวนอนแทนที่จะบีบจนอ่านไม่ออกบนมือถือ */}
                <div className="table-responsive">
                <table className="w-100 mt-3" style={{ borderCollapse: "collapse", minWidth: 610 }}>
                  <thead>
                    <tr style={{ background: "#6b7280", color: "#fff" }}>
                      <th style={{ padding: "8px 6px", textAlign: "center", width: 50 }}>ลำดับ</th>
                      <th style={{ padding: "8px 10px", textAlign: "center" }}>รายการ</th>
                      <th style={{ padding: "8px 6px", textAlign: "center", width: 70 }}>จำนวน</th>
                      <th style={{ padding: "8px 6px", textAlign: "center", width: 100 }}>ราคา / หน่วย</th>
                      <th style={{ padding: "8px 6px", textAlign: "center", width: 110 }}>ราคา</th>
                      <th style={{ width: 34 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.items.map((it, idx) => {
                      const amount = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
                      return (
                        <tr key={idx} style={{ borderBottom: "1px solid #eef2f7", verticalAlign: "top" }}>
                          <td className="align-top" style={{ padding: "8px 6px", textAlign: "center" }}>{idx + 1}</td>
                          <td className="align-top" style={{ padding: "8px 10px" }}>
                            <textarea
                              value={it.name}
                              onChange={(e) => setItem(idx, "name", e.target.value)}
                              placeholder="ชื่อรายการ"
                              rows={Math.max(1, (it.name || "").split("\n").length)}
                              style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "2px 6px", outline: "none", width: "100%", fontSize: 15, fontWeight: 700, whiteSpace: "pre-wrap", resize: "vertical" }}
                              onFocus={(e) => (e.target.style.borderColor = "#0f6bdc")}
                              onBlur={(e) => (e.target.style.borderColor = "#cbd5e1")}
                            />
                            <textarea
                              value={it.detail}
                              onChange={(e) => setItem(idx, "detail", e.target.value)}
                              placeholder="รายละเอียด (ขึ้นบรรทัดใหม่/ใส่หัวข้อย่อยได้)"
                              rows={Math.max(2, (it.detail || "").split("\n").length)}
                              style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "4px 6px", outline: "none", width: "100%", marginTop: 4, fontSize: 13, color: "#374151", whiteSpace: "pre-wrap", resize: "vertical" }}
                              onFocus={(e) => (e.target.style.borderColor = "#0f6bdc")}
                              onBlur={(e) => (e.target.style.borderColor = "#cbd5e1")}
                            />
                          </td>
                          <td className="align-top" style={{ padding: "8px 6px" }}>{inlineInput(it.qty, (v) => setItem(idx, "qty", v), { type: "number", align: "center" })}</td>
                          <td className="align-top" style={{ padding: "8px 6px" }}>{inlineInput(it.unit_price, (v) => setItem(idx, "unit_price", v), { type: "number", align: "right" })}</td>
                          <td className="align-top" style={{ padding: "8px 6px", textAlign: "right", fontWeight: 600 }}>{money(amount)}</td>
                          <td className="align-top" style={{ padding: "8px 2px", textAlign: "center" }}>
                            <button className="btn btn-sm btn-link text-danger p-0" title="ลบรายการ" onClick={() => removeItem(idx)} disabled={doc.items.length === 1}><i className="fa-solid fa-xmark" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                <button className="btn btn-sm btn-outline-primary mt-2" onClick={addItem}><i className="fa-solid fa-plus me-1" /> เพิ่มรายการ</button>

                <div className="row mt-4 pt-3" style={{ borderTop: "1px solid #cbd5e1" }}>
                  <div className="col-12 col-sm-6">
                    {/* ใบสั่งซื้อไม่มีบัญชีรับเงินของบริษัท ใช้ช่องหมายเหตุ/เงื่อนไข (เช่น กำหนดส่ง, เครดิตเทอม) แทน */}
                    {isPurchaseOrder(doc.doc_type) ? (
                      <>
                        <div className="fw-bold mb-1">หมายเหตุ / เงื่อนไข</div>
                        <textarea value={doc.note} onChange={(e) => setField("note", e.target.value)} placeholder="เช่น กำหนดส่งสินค้า, เงื่อนไขการชำระเงิน, สถานที่จัดส่ง" rows={3}
                          style={{ border: "1px dashed #cbd5e1", borderRadius: 4, padding: "2px 6px", outline: "none", width: "100%", fontSize: 14, resize: "vertical" }} />
                      </>
                    ) : (
                      <>
                        <div className="fw-bold mb-1">ช่องทางการชำระเงิน</div>
                        <div className="d-flex align-items-center gap-2 mb-1 codoc-field-row">
                          <span className="fw-bold flex-shrink-0" style={{ width: 70 }}>ชื่อบัญชี:</span>
                          {inlineInput(doc.payment_account_name, (v) => setField("payment_account_name", v), { placeholder: "ชื่อบัญชี", width: 240 })}
                        </div>
                        <div className="d-flex align-items-center gap-2 mb-1 codoc-field-row">
                          <span className="fw-bold flex-shrink-0" style={{ width: 70 }}>เลขบัญชี:</span>
                          {inlineInput(doc.payment_account_no, (v) => setField("payment_account_no", v), { placeholder: "เลขบัญชี", width: 150 })}
                          {inlineInput(doc.payment_bank, (v) => setField("payment_bank", v), { placeholder: "ธนาคาร", width: 130 })}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="d-flex justify-content-end align-items-baseline gap-3"><span className="fw-bold">ราคารวม</span><span style={{ width: 130, textAlign: "right" }}>{money(totals.subtotal)} บาท</span></div>
                    <div className="d-flex flex-wrap justify-content-end align-items-center gap-3 mt-1">
                      <span className="fw-bold d-flex align-items-center gap-1">
                        <input type="checkbox" className="form-check-input mt-0" checked={!!doc.vat_enabled} onChange={(e) => setField("vat_enabled", e.target.checked)} title="เปิด/ปิด VAT" />
                        ภาษีมูลค่าเพิ่ม {inlineInput(doc.vat_percent, (v) => setField("vat_percent", v), { type: "number", align: "center", width: 46, style: doc.vat_enabled ? {} : { opacity: 0.4 } })}%
                      </span>
                      <span style={{ width: 130, textAlign: "right", opacity: doc.vat_enabled ? 1 : 0.4 }}>{money(totals.vat_amount)} บาท</span>
                    </div>
                    <div className="d-flex flex-wrap justify-content-end align-items-center gap-3 mt-1">
                      <span className="fw-bold d-flex align-items-center gap-1">
                        <input type="checkbox" className="form-check-input mt-0" checked={!!doc.wht_enabled} onChange={(e) => setField("wht_enabled", e.target.checked)} title="เปิด/ปิด หัก ณ ที่จ่าย" />
                        ภาษีหัก ณ ที่จ่าย {inlineInput(doc.wht_percent, (v) => setField("wht_percent", v), { type: "number", align: "center", width: 46, style: doc.wht_enabled ? {} : { opacity: 0.4 } })}%
                      </span>
                      <span style={{ width: 130, textAlign: "right", opacity: doc.wht_enabled ? 1 : 0.4 }}>{totals.wht_amount ? `-${money(totals.wht_amount)}` : money(0)} บาท</span>
                    </div>
                    <div className="d-flex justify-content-end align-items-baseline gap-3 mt-1 pt-1" style={{ borderTop: "1px solid #cbd5e1" }}><span className="fw-bold">ราคารวมทั้งหมด</span><span className="fw-bold" style={{ width: 130, textAlign: "right" }}>{money(totals.grand_total)} บาท</span></div>
                    <div className="text-end fst-italic text-secondary mt-1" style={{ whiteSpace: "nowrap" }}>({totals.amount_text})</div>
                  </div>
                </div>

                {/* ผู้ลงนาม: เลือกจากพนักงานที่เป็น super user (ดึงชื่อ + ลายเซ็นให้) หรือพิมพ์ชื่อเองในวงเล็บด้านล่าง */}
                <div className="mt-4 d-flex flex-wrap align-items-center justify-content-end gap-2 codoc-field-row">
                  <span className="small text-secondary">ผู้ลงนาม :</span>
                  <div style={{ minWidth: 260 }}>
                    <Select
                      className="form-control"
                      value={doc.signer_employee_uid}
                      options={superUserOptions}
                      placeholder="เลือกจาก super user (ใช้ลายเซ็นในระบบ) หรือพิมพ์เอง"
                      onChange={(v) => selectSignerEmployee(v)}
                    />
                  </div>
                  {doc.signer_employee_uid ? (
                    <button type="button" className="btn btn-sm btn-light" onClick={() => selectSignerEmployee("")}>
                      <i className="fa-solid fa-xmark me-1" />ล้าง
                    </button>
                  ) : null}
                  {doc.signer_employee_uid && !doc.signer_signature_url ? (
                    <span className="small text-danger">พนักงานคนนี้ยังไม่มีลายเซ็นในระบบ (จะแสดงเฉพาะชื่อ)</span>
                  ) : null}
                </div>
                <div style={{ width: 300, marginLeft: "auto", textAlign: "center" }} className="mt-2 codoc-signer-box">
                  <div className="d-flex align-items-end justify-content-center" style={{ height: 64, borderBottom: "1px dotted #94a3b8" }} title="พื้นที่สำหรับลายเซ็น">
                    {doc.signer_signature_url ? <img src={doc.signer_signature_url} alt="" style={{ maxHeight: 62, maxWidth: 220, objectFit: "contain" }} /> : null}
                  </div>
                  <div className="mt-1 d-flex align-items-center justify-content-center gap-1">
                    <span>(</span>
                    {/* พิมพ์ชื่อเองทับ = ยกเลิกการผูกพนักงานและลายเซ็นที่เลือกไว้ */}
                    {inlineInput(doc.signer_name, (v) => setDoc((c) => ({ ...c, signer_name: v, signer_employee_uid: "", signer_signature_url: "" })), { placeholder: "ชื่อผู้ลงนาม", align: "center", width: 220 })}
                    <span>)</span>
                  </div>
                  <div className="small text-secondary mt-1">{docDate(doc.doc_date)}</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* เมนู "..." ของแถวในตาราง */}
      {actionMenu && (
        <div className="drive-context-menu" style={{ top: actionMenu.y, left: actionMenu.x }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={async () => {
              const d = actionMenu.item;
              setActionMenu(null);
              setPreviewDoc(await withSignature(mapApiDoc(d)));
            }}
          >
            <i className="fa-solid fa-eye" style={{ width: 16 }} />
            ดูตัวอย่าง
          </button>
          <button
            type="button"
            style={{ color: "#0c76e5", fontWeight: 600 }}
            onClick={() => {
              const d = actionMenu.item;
              setActionMenu(null);
              openEdit(d);
            }}
          >
            <i className="fa-solid fa-pen" style={{ width: 16 }} />
            แก้ไข
          </button>
          <button
            type="button"
            onClick={() => {
              const d = actionMenu.item;
              setActionMenu(null);
              downloadDoc(d);
            }}
          >
            <i className="fa-solid fa-file-pdf" style={{ width: 16 }} />
            ดาวน์โหลด PDF
          </button>
          <div className="drive-context-divider" />
          <button
            type="button"
            className="is-danger"
            onClick={() => {
              const d = actionMenu.item;
              setActionMenu(null);
              deleteDoc(d);
            }}
          >
            <i className="fa-solid fa-trash" style={{ width: 16 }} />
            ลบ
          </button>
        </div>
      )}

      {/* preview modal */}
      <Modal show={!!previewDoc} onHide={() => setPreviewDoc(null)} centered size="lg" contentClassName="border-0">
        <Modal.Header closeButton className="bg-light"><Modal.Title className="fw-bold h6 mb-0">ตัวอย่างเอกสาร</Modal.Title></Modal.Header>
        <Modal.Body style={{ background: "#e9edf3", overflowX: "hidden" }}>
          {previewDoc && (
            <div ref={previewOuterRef} style={{ position: "relative", width: "100%", overflow: "hidden" }}>
              <div ref={previewInnerRef} style={{ width: 794, transform: `scale(${previewScale})`, transformOrigin: "top left" }}>
                <DocumentView doc={previewDoc} shadow />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <button className="btn btn-light" onClick={() => setPreviewDoc(null)}>ปิด</button>
          {previewDoc && <button className="btn fw-semibold text-white" style={{ background: "#dc2626" }} onClick={() => downloadDoc(previewDoc)}><i className="fa-solid fa-file-pdf me-1" /> ดาวน์โหลด PDF</button>}
        </Modal.Footer>
      </Modal>

      {/* hidden render target สำหรับทำ PDF */}
      <div style={{ position: "absolute", left: -99999, top: 0 }} aria-hidden="true">
        <div ref={printRef}><DocumentView doc={printDoc} /></div>
      </div>
    </AppShell>
  );
}
